// i18n area 'storage' (REMODEL R1 #214, 2026-08-26 — the datapool surface is
// DEAD, replaced by the two first-class cluster species; grew out of the M2d
// refactor, docs/multiscsi-bucket-model-2026-08-03.md): sections
// storage.clusters (the ONE Storage clusters list), storage.volumes (cluster/mode/replicas),
// storage.snapshots (M2b, untouched conceptually), storage.hosts (initiator
// groups, survives from M2c) and storage.targets (named exports, replacing
// storage.mappings). The nav.* labels for the Storage group live in common.ts:
// dictionaries.ts composes sections with a SHALLOW spread, so a second `nav`
// branch here would clobber the common one instead of merging into it.

export const en = {
  storage: {
    clusters: {
      title: 'Storage clusters',
      createTitle: 'Create storage cluster',
      name: 'Name',
      nameHint: 'Lowercase letters, digits and dashes',

      // REMODEL R1 (#214, 2026-08-26): the datapool surface is DEAD — the two
      // engine halves are first-class resources (CephCluster and
      // MultiscsiCluster) fused into this ONE list, each row tagged with its
      // kind. The `use` verb and the unbound state died with the rename:
      // creation BIRTHS the fabric.
      // REMODEL R5 (#214, 2026-08-27 — ratifica §3): TWO DISTINCT PAGES —
      // /storage/ceph-clusters and /storage/multiscsi-clusters; the fused
      // list died with its route (no alias, alpha doctrine).
      createTitleCeph: 'Create Ceph cluster',
      createTitleMultiscsi: 'Create MultiSCSI cluster',
      capacityBar: {
        title: 'Promised vs used',
        promised: 'Promised raw (size × weight, declared included)',

        // ENEA-A-R13: the NET side of the promise (Σ declared sizes) beside the raw.
        promisedNet: 'net',
        used: 'Used (observed)',
        capacity: 'Capacity (observed)',
        admittable: 'Still admittable',
        notObserved:
          'Capacity not observed yet — the admission refuses rather than guessing (retry once the cluster reports)'
      },
      explorer: {
        error: 'Explorer query failed',
        retry: 'Retry',
        refresh: 'Refresh',
        tabSalute: 'Health',
        tabDemoni: 'Daemons',
        tabPool: 'Pools',
        tabCrush: 'CRUSH',
        checksTitle: 'Health checks (mon)',
        noChecks: 'No active checks — the mon reports HEALTH_OK.',
        checkCode: 'Check',
        checkSeverity: 'Severity',
        checkSummary: 'Summary',
        osdsTitle: 'OSD census (explorer)',
        osdClass: 'Class',
        osdWeight: 'Weight / reweight',
        osdUtilization: 'Utilization',
        osdLatency: 'Latency (apply/commit)',
        osdOutAction: 'Mark out (drain before maintenance)',
        osdInAction: 'Mark back in',
        osdOutTitle: 'Mark {osd} out',
        osdInTitle: 'Mark {osd} back in',
        osdOutWarning:
          'The cluster re-replicates away from this OSD — data movement starts now. The vdisk claim stays: out is NOT a membership removal.',
        osdInWarning: 'The OSD rejoins placement — the return path of out (data movement).',
        poolsTitle: 'All pools',
        poolsHint:
          'The explorer shows the truth: every pool, system pools included — it is the volumes surface that hides them.',
        poolName: 'Pool',
        poolOwner: 'Owner',
        poolOwnerVolume: 'volume',
        poolOwnerSystem: 'system',
        poolProfile: 'size/min_size',
        poolPgNum: 'pg_num',
        poolApplication: 'Application',
        poolUsage: 'Usage',
        poolObjectsCount: '{count} object(s)',
        objectsAction: 'Explore objects (rados ls)',
        objectsTitle: 'Objects of {pool}',
        objectsHint: 'Paginated rados ls — a diagnostic tool, never a data path.',
        objectsPrefix: 'Name prefix',
        objectsSearch: 'Search',
        objectsEmpty: 'No objects match.',
        objectsMore: 'Load more',
        deepScrubAction: 'Deep-scrub on demand',
        deepScrubTitle: 'Deep-scrub pool {pool}',
        deepScrubHint:
          "Requests an on-demand deep scrub of this pool — a mon command, minimal cost; the scrub itself runs on the cluster's own schedule.",
        crushTitle: 'Decoded CRUSH tree',
        crushRules: 'Rules',
        osdFlagsAction: 'OSD flags (maintenance)',
        osdFlagsTitle: 'OSD flags of {name}',
        osdFlagsWarning:
          "These flags SUSPEND the cluster's self-healing (noout before a node reboot avoids a rebalance storm; pause is the emergency brake). Clear them when the maintenance ends.",
        osdFlagsFlag: 'Flag',
        osdFlagsValue: 'Action',
        osdFlagsValueHint: 'Explicit, no default',
        osdFlagsSet: 'Set',
        osdFlagsClear: 'Clear'
      },
      editTitle: 'Edit cluster {name}',
      renameHint: 'Cosmetic: internal references are id-based and the on-device names embed the uuid, not the name',

      // VOL-CRYPT-CLUSTER (2026-09-04): encryption is the CLUSTER's — chosen
      // at creation, immutable; every vdisk that joins is encrypted, volumes
      // inherit. The old per-OSD/per-vdisk keys died with the doctrine.
      encryption: 'Encryption at rest',

      // ENEA-A-R4 (2026-10-23): the floating addresses of the cluster's file gateways.
      gatewayAddresses: 'Gateway floating addresses',
      gatewayAddressesNone: "none — clients use the nodes' own addresses",
      gatewayAddressesHint:
        "Addresses of the CLUSTER, distributed round-robin over the member gateways (each has a home) and taken over by a survivor when the holder's lease goes stale; with the cluster setting gatewayFailover.rebalance = automatic they return home after the node is healthy again. What moves is TCP connectivity: hard NFS mounts pause and resume, NFSv4 state is re-established by the client (Ganesha opens a cluster-wide grace first); SMB does not follow.",
      gatewayAddressesEditHint:
        'One bare IPv4 per line (max 16), inside a declared client subnet, never a node, binding or share address. Changes converge on the reconcile pass.',
      gatewayAddressesInvalid: 'Not a bare IPv4 literal',

      // ENEA-B-R15 (2026-10-24): the tenant each floating address is assigned to.
      gatewayAddressScopes: 'Tenant of each gateway address',
      gatewayAddressScopeCommon: 'common — every tenant',
      gatewayAddressScopeChip: 'tenant {scope}',
      gatewayAddressScopesHint:
        'An address assigned to a scope is announced ONLY to the shares and stores of that scope and its descendants (the nearest assigned ancestor wins); a tenant with assigned addresses never sees the common ones. Removing an assigned address from the list drops the assignment; deleting a scope with assigned addresses is refused (409).',

      // ENEA-B-R14 (2026-10-24): the throughput ceiling per floating gateway address.
      gatewayQos: 'Gateway address QoS',
      gatewayQosReadBps: 'Read ceiling per gateway address (MiB/s)',
      gatewayQosWriteBps: 'Write ceiling per gateway address (MiB/s)',
      gatewayQosHint:
        'Every floating address gets the same ceiling, applied by the node that HOLDS it (the shaping follows the address) as a leaf of the network QoS tree: reads shaped, writes policed. It covers every share served from that address — the per-GATEWAY answer where a Ceph share has no address of its own. Empty = no limit; floor 1 MiB/s.',
      gatewayQosNone: 'no ceiling',
      gatewayQosShaped: 'QoS: shaped',
      gatewayQosNotShaped: 'QoS: NOT shaped',
      gatewayQosNotReported: 'QoS: not reported',
      gatewayQosNotReportedHint:
        'R14 declared: no observation row carries the per-address leaf state yet (the holder keeps it in its reconcile log) — «not reported» never means «not shaped».',
      leaseStates: {
        fresh: 'held',
        stale: 'stale — takeover due',
        none: 'not taken yet'
      },
      encryptionModes: {
        none: 'Plain',
        luks2: 'LUKS2 — every member vdisk'
      },
      encryptionHint:
        'Chosen at creation and immutable: every vdisk that joins this cluster is LUKS2-encrypted with a platform-minted keystore key (export the recovery keys to the safe from System → Credentials); volumes inherit it. A different choice is a NEW cluster and moving the data with backup/restore.',
      encryptionCreateHint:
        'Every vdisk that joins is LUKS2-formatted at the join with a platform-minted keystore key — nobody types a passphrase; volumes inherit. Off by default on MultiSCSI.',
      encryptionCreateHintCeph:
        'Every OSD (= every member vdisk) is dm-crypt/LUKS2 with a platform-minted keystore key — nobody types a passphrase; volumes inherit. On by default on Ceph.',
      encryptionImmutable:
        'IMMUTABLE — choose now: a cluster is born encrypted or plain, forever. There is no in-place migration or re-encryption; a different choice later means a NEW cluster and moving the data with backup/restore.',
      encryptionOffWarning:
        'Plain cluster: every OSD created in it lands its data on disk in the clear, forever — a cluster is born encrypted or plain and cannot change later (a NEW cluster + backup/restore is the road).',
      kind: 'Kind',
      kinds: {
        multiscsi: 'MultiSCSI',
        ceph: 'Ceph'
      },
      phase: 'Phase',
      phases: {
        initializing: 'Initializing',
        ready: 'Ready',
        degraded: 'Degraded',
        error: 'Error',
        destroying: 'Destroying'
      },
      wrongPhase: 'This action needs the cluster ready or degraded',
      destroying: 'The cluster is being torn down',
      capacity: 'Capacity',
      used: 'Used',
      provisioned: 'Provisioned',
      members: 'Members',
      observedAt: 'Observed at',
      notObserved: 'Not yet observed',
      tabOverview: 'Overview',
      tabVdisks: 'Vdisks',
      vdisksEmpty: 'No vdisk observations yet — they land once the fabric initialization reaches the member nodes',
      vdisk: {
        node: 'Node',
        vdisk: 'Vdisk',
        vg: 'Volume group',
        state: 'State',
        size: 'Size',
        observedAt: 'Observed at'
      },
      create: {
        kind: 'Kind',
        kindHint:
          'MultiSCSI (iSCSI SAN) or Ceph (native RBD) — creating births the whole fabric on this kind: the choice is final',
        taskHint:
          'Creation starts the fabric right away (phase Initializing) and this dialog follows the birth task to its end.',
        cephHint:
          'Ceph turns the members into a whole Ceph cluster: every member vdisk becomes an OSD, a monitor runs on every member node, and volumes are RBD images in the cluster pool.',
        cephTooFewNodes:
          'Ceph needs at least 3 member vdisks on 3 distinct nodes — the selection spans {nodes} node(s): the create will be refused (single-node Ceph is a dev-only backend relaxation).',
        membersTitle: 'Member vdisks',
        membersHint: 'Pick the vdisks contributing to this cluster — the fabric is built on them at creation.',
        noVdisks: 'No node has a ready unclaimed vdisk yet — make one from the Cluster page (Create vdisk) first.',
        claimed: 'Claimed'
      },
      ceph: {
        tabTitle: 'Ceph Health',
        notCeph: 'This is not a Ceph cluster',
        notObserved: 'No Ceph observation yet — the sweep from a mon-hosting member node has not landed',
        health: 'Cluster health',
        healths: {
          healthy: 'Healthy (HEALTH_OK)',
          degraded: 'Degraded (HEALTH_WARN)',
          error: 'Error (HEALTH_ERR)'
        },
        fsid: 'fsid',
        monsTitle: 'Monitors',
        monInQuorum: 'in quorum',
        monOutOfQuorum: 'out of quorum',
        monNotObserved: 'not yet observed',
        mdsTitle: 'MDS — the CephFS map census',
        mdsNone: 'no MDS in the map (no CephFS on this cluster)',
        osdsTitle: 'OSDs — one per member vdisk',
        osd: {
          node: 'Node',
          vdisk: 'Vdisk',
          id: 'OSD id',
          up: 'Up',
          in: 'In'
        },
        osdUp: 'up',
        osdDown: 'down',
        osdIn: 'in',
        osdOut: 'out',
        capacityTitle: 'Capacity',
        capacityTotal: 'Total (raw)',
        capacityUsed: 'Used',
        capacityAvail: 'Available',

        // #181 ondata 5 (NAS-1): the file-fabric halves. (Le chiavi mds* di
        // quell'era sono morte con R5 e rinate sopra con MDS-CENSIMENTO.)
        fsTitle: 'Filesystem (tvstor-fs)',
        fsNone: 'Not born yet — the ONE filesystem arrives with the first file share; once born it stays until Destroy',
        fsShares: '{count} share(s)',
        observedAt: 'Observed at'
      },

      // #181 ondata 5 (SCRUB-MS #94): policy + on-demand pass + status.
      scrub: {
        tabTitle: 'Scrub',
        notMultiscsi: 'Scrub is a MultiSCSI surface: Ceph deep-scrubs natively (its health rides the Ceph tab)',
        action: 'Run a consistency pass over the md arrays',
        title: 'Scrub cluster {name}',
        submit: 'Start the pass',
        mode: 'Mode',
        modeHint: 'Explicit, no default: check counts and touches nothing; repair is an operator act',
        modes: {
          check: 'Check — count mismatches, touch nothing',
          repair: 'Repair — let md rewrite one copy over another'
        },
        checkHint:
          "The pass runs as a task, one node-affine child per member node; progress and the mismatch census land in this cluster's Scrub tab. A pass already in flight answers 409.",
        repairWarning:
          "On a mirror md CANNOT know which copy is sane: repair copies the FIRST member over the others (arbitrary-copy) or recomputes parity over possibly-rotten data blocks (parity-recompute) — read the repair semantics column in the Scrub tab for THESE arrays before confirming. Sudo plus the cluster's name typed back.",
        confirmLabel: 'Cluster name',
        policyTitle: 'Policy',
        policyTitleFor: 'Scrub policy of {name}',
        policyManualOnly: 'Manual only — no scheduled pass (the policy is null)',
        policyLine: 'Every {days} day(s), starting at {hour}:00 UTC, window {duration} h — speed cap: {speed}',
        policyEdit: 'Edit policy',
        policyMode: 'Scheduling',
        policyManualOption: 'Manual only (no schedule)',
        policyScheduledOption: 'Scheduled window',
        everyDays: 'Every N days',
        startHour: 'Start hour (UTC)',
        startHourHint: '0-23, UTC',
        durationHours: 'Window (hours)',
        durationHint: '1-24 — a pass overrunning the window is STOPPED and declared partial',
        maxSpeed: 'Speed cap (KiB/s per array)',
        maxSpeedHint: 'Empty = kernel default',
        speedKernelDefault: 'kernel default',
        statusTitle: 'Status per array',
        statusNone: 'No scrub observation yet — run a pass, or wait for the scheduled window',
        colArray: 'Array',
        colAction: 'Kernel action',
        colMismatches: 'Mismatches',
        colLastResult: 'Last result',
        colRepairSemantics: 'Repair semantics',

        // «Non-zero is an ALARM, never a log line» (contract).
        mismatches: '{count} MISMATCHES',
        results: {
          completed: 'completed',
          partial: 'PARTIAL — not a verification'
        },
        repairSemanticsLabels: {
          'arbitrary-copy': 'arbitrary copy',
          'parity-recompute': 'parity recompute'
        },
        repairSemanticsHints: {
          'arbitrary-copy': 'raid1/10: md copies the FIRST member over the others — it cannot know which copy is sane',
          'parity-recompute': 'raid5/6: parity is recomputed from the data blocks — a rotten data block is crystallized'
        },
        skippedTitle: 'Arrays deliberately NOT scrubbed (with the reason):'
      },

      // The ONE membership growth action — both kinds since REMODEL R1
      // (#209 opened the multiscsi corsia).
      addMember: {
        action: 'Add a member',
        title: 'Add a member to {name}',
        submit: 'Add member',
        cephHint:
          'One new {node, vdisk} member = one new OSD. The vdisk must be a READY, unclaimed vdisk of the chosen node; the cluster rebalances onto the new OSD by itself. Removing a member is deliberately not offered.',
        multiscsiHint:
          'One new {node, vdisk} member: the ensure-idempotent init family re-runs over the grown membership — the new member converges, the rest no-op. Removing a member is deliberately not offered.',
        node: 'Node',
        vdisk: 'Vdisk',
        alreadyMember: 'already a member',
        claimed: 'claimed by another cluster',
        notReady: 'not ready'
      },
      deleteCephBlock:
        'A Ceph cluster tears down only through Destroy (the danger ladder) — plain delete always answers 422',
      destroy: {
        action: 'Destroy the Ceph cluster',
        title: 'Destroy cluster {name}',
        warning:
          'This tears the WHOLE Ceph cluster down: every monitor and OSD on the member nodes is stopped and unregistered. With "Wipe devices" the member vdisks are WIPED — every byte of every RBD image is gone forever.',
        wipeLabel: 'Member devices',
        wipeYes: 'Wipe devices — bytes destroyed, vdisks reusable',
        wipeNo: 'Keep bytes intact (forensics) — vdisks stay unusable until wiped',
        wipeHint: 'Mandatory explicit decision — there is no default',
        confirmLabel: 'Cluster name',
        confirmHint: 'Type {name} to confirm the teardown',
        submit: 'Destroy'
      },

      // MSGR2-MIGRAZIONE (2026-08-25): the one-way move of a pre-Fase-A
      // cluster onto the msgr2 secure form.
      migrateSecure: {
        action: 'Migrate to msgr2 secure',
        title: 'Migrate {name} to msgr2 secure',
        warning:
          "The migration rolling-restarts every daemon of a serving cluster to reach the ONE msgr2 secure form (byte-identical to a born-secure cluster). It is idempotent: an already-secure cluster resolves with a no-work task, and a mid-way failure resumes by running it again. Sudo plus the cluster's name typed back.",
        confirmLabel: 'Cluster name',
        submit: 'Migrate'
      }
    },
    volumes: {
      title: 'Volumes',
      createTitle: 'Create volume',
      name: 'Name',
      nameHint: 'Lowercase letters, digits and dashes',
      datapool: 'Cluster',
      mode: 'Mode',
      modes: {
        mirror: 'Mirror'
      },
      replicas: 'Redundancy',
      copies: 'Copies (X)',
      copiesHint: 'Total mirror legs, one per distinct vdisk',
      copiesCephHint: "Physical copies — the volume's own pool size (1 is dev-only, behind the single-node relaxation)",
      minInSync: 'Min in sync (Y)',
      minInSyncHint: 'Below Y in-sync legs the volume stops accepting writes',
      sizeGiB: 'Size (GiB)',

      // ENEA-A-R13 «erasure coding» (2026-10-24): the raw footprint of the
      // promise (size × weight — the very quantity the admission sums) and
      // the two-form redundancy of the create dialog.
      rawBytes: 'Raw footprint',
      rawBytesHint: 'What this promise costs on the cluster: size × copies, or size × (k + m) / k with erasure coding',
      redundancyForm: {
        mode: 'Protection',
        replicated: 'Replicated',
        erasure: 'Erasure coding',
        optIn:
          'Replicated is the default; erasure coding is an opt-in per volume — EC 4+2: 300 TB net cost 450 raw instead of 600 at 2 copies',
        erasureCephOnly:
          'Erasure coding is a Ceph (RADOS) concept — a MultiSCSI volume is an md mirror on the member vdisks you chose',
        k: 'Data chunks (k)',
        kHint:
          '2..16 — every chunk lands on a distinct OSD host (failure domain host); the data pool is born erasure-coded with overwrites enabled, a replicated 3/2 pool beside it keeps the metadata',
        m: 'Coding chunks (m)',
        mHint:
          '1..8 — the volume survives m lost hosts; Ceph sets min_size = k + min(1, m−1) (measured: 2+1 → 2, 4+2 → 5): with m = 1 one host down leaves the pool serving with NO spare parity, with m ≥ 2 one parity stays spare. The offer’s choice is 4+2',
        kmHosts: 'k + m ({km}) exceeds the cluster’s OSD hosts ({hosts}) — each chunk needs its own host',
        capacityLine: 'Net {net} → raw {raw} on the cluster ({weight})',
        netAdmittable: 'Net headroom at this policy: {net}'
      },
      phase: 'Phase',
      phases: {
        // REMODEL R2 (#214): the DECLARED model — a promise until the first
        // consumer materializes it.
        declared: 'Declared — no bytes yet',
        materializing: 'Materializing',
        ready: 'Ready',
        degraded: 'Degraded',
        blocked: 'Blocked — writes paused',
        error: 'Error',
        deleting: 'Deleting'
      },
      specie: 'Species',
      species: {
        lun: 'LUN',
        filesystem: 'Filesystem',
        object: 'Object'
      },

      // REMODEL R5 (#214): the declared model spoken on the surface — the
      // callout while declared, the not-materialized reason on disabled
      // verbs (mirrors the backend's 422 volume_not_materialized).
      declaredCallout:
        'Not materialized — the volume materializes at its FIRST consumer: export it with a Target (LUN), or let a FileShare/ObjectStore consume it.',
      notMaterializedBlock:
        'Not materialized yet — this verb needs materialized bytes (the volume materializes at its first consumer)',
      materializedAt: 'Materialized at',
      mdState: 'RAID state',

      // RESUME-2 fase C (2026-08-13): the TORN marker — a volume half-way
      // through a restore. Shown in the list next to the phase, because a torn
      // volume that looks like every other one is exactly the lie this round
      // removes.
      torn: 'Torn',
      tornChip: 'TORN',
      tornWhole: '—',
      tornHint:
        'A restore started writing into this volume and never completed: its bytes are neither the old image nor the restored one. Exports are refused. Re-run the restore to completion (it resumes from its checkpoint), or delete the volume.',
      tornDetail: 'Restore task {task} on {node}, since {since}',
      tornDetailUnknown: 'The stored marker cannot be read — the volume is torn, the details are unknown',
      authorityNode: 'Authority node',
      exportedBy: 'Exported by',
      notExported: 'Not exported',
      exportedBlock: 'The volume is exported by target {targets} — delete the target first',
      shareBackedDeleteBlock:
        'This volume backs a file share — delete the SHARE (its dialog carries the explicit destroy-data decision); a direct delete is refused (409)',

      // M4b (#189): the DR-replica badge — the marker used to speak only
      // through the expand/qos refusals.
      replicaChip: 'DR REPLICA',
      replicaHint:
        'Read-only replica materialized from cluster {cluster} (last received stamp: {stamp}) — the receive stream is its only writer: targets, revert, resize and restore-overwrite are refused. Promotion of the DR copy is a later round; delete is allowed (the source reseeds full if the link still lists it).',
      observedAt: 'Observed at',
      notObserved: 'Not yet observed',
      tabOverview: 'Overview',
      tabLegs: 'Legs',
      legsEmpty: 'No leg observations yet',
      legs: {
        vdisk: 'Vdisk',
        node: 'Node',
        vg: 'Volume group',
        lv: 'Logical volume',
        state: 'State',
        reads: 'Reads',
        fence: 'Fence',
        at: 'Observed at',

        // PERF-1 (#189): the md (W) mark, observed by the sweep.
        writeMostlyChip: 'write-mostly (W)',
        writeMostlyHint:
          "md holds this leg write-mostly: reads are served by the authority's LOCAL leg while it is healthy — purely a read-routing hint, writes stay synchronous on every leg (write-behind is forbidden). Remote legs carry the mark at every assembly; the local leg never does."
      },

      // M2e (ratifiche Luca 2026-08-03, docs/multiscsi-two-node-failure-notes.md):
      // the Authority / Failover tab — lease render, failover proof health,
      // per-leg fence marks, manual failover and the declared-data-loss
      // force-promote.
      authority: {
        tabTitle: 'Authority / Failover',
        holder: 'Authority node',
        unassigned: 'Not yet assigned',
        lease: 'Lease',
        leaseExpiresAt: 'Lease horizon',
        epoch: 'Epoch',
        phases: {
          held: 'Held',
          stale: 'Stale',
          failingOver: 'Failing over'
        },
        phaseHints: {
          held: 'The lease is fresh — the holder renewed within the TTL',
          stale: 'Renewal lapsed past the TTL: the holder stopped renewing — the volume is a takeover candidate',
          failingOver: 'A failover task is transferring the authority (epoch bump in flight)'
        },
        health: 'Failover proof health',
        healthHint:
          'Whether an automatic takeover could prove itself right now — a witness grant or a verified fencing of the peer; a timeout alone is never proof.',
        witness: 'Witness',
        peerFencing: 'Peer fencing',
        reachable: 'Reachable',
        unreachable: 'Unreachable',
        notConfigured: 'Not configured',
        legsTitle: 'Legs',
        leg: 'Leg {index}',
        fenced: 'Stale-fenced',
        fencedHint:
          'The degrade intent was committed before md resumed acking degraded: this leg misses writes acked to clients. Never auto-promotable — it returns only as a resync target; the mark clears on completed resync.',
        fencedAt: 'Fenced at {at}',
        failover: 'Failover',
        failoverTitle: 'Fail volume {name} over',
        failoverHint:
          'The takeover needs PROOF the current holder is stopped — a witness grant or a verified fencing-cascade outcome, never a timeout alone. Without proof the action is refused (the honest manual floor).',
        failingOverBlock: 'A failover is already in flight',
        targetNode: 'Target node',
        targetNodeAuto: 'Automatic placement',
        targetNodeHint: 'Only nodes carrying a non-fenced leg are eligible',
        failoverSubmit: 'Failover',
        forcePromote: 'Force-promote',
        forcePromoteTitle: 'Force-promote leg {index} of {name}',
        forcePromoteWarning:
          'DECLARED DATA LOSS: this leg is stale-fenced — it misses writes that WERE acknowledged to clients after its fence. Promoting it makes it the only truth: every other leg is fenced against the new epoch and resynced from it, and the discarded acked writes vanish forever.',
        forcePromoteConfirmLabel: 'Volume name',
        forcePromoteConfirmHint: 'Type {name} to confirm the declared data loss',
        forcePromoteSubmit: 'Force-promote'
      },
      create: {
        datapoolHint: 'Only storage clusters in the Ready or Degraded phase accept new volumes',
        noDatapools: 'No storage cluster is serving — create one and let its fabric initialize first.',
        declaredHint:
          "The volume is born DECLARED: a synchronous promise of capacity, admitted against the cluster's observed capacity (size × copies). Nothing touches a device until its first consumer — a target, or a share/store backing — materializes it."
      },

      // M5d (2026-08-04): online expansion — grow only, stated in the dialog.
      expand: {
        action: 'Expand',
        title: 'Expand volume {name}',
        current: 'Current size: {size} GiB',
        newSize: 'New size (GiB)',
        growOnlyHint:
          'GROW ONLY here — the new size must be larger than the current one; the shrink verse is its own sudo, confirm-typed action (#34). The volume keeps serving while it grows.',
        rescanHint:
          'TVStor grows the LUN, not your data: iSCSI initiators must rescan to see the new capacity (e.g. iscsiadm -m node -R), and the partition/filesystem inside the consumer must be grown by its owner.',
        tooSmall: 'Must be larger than the current {size} GiB',
        notReady: 'Expand needs a Ready volume — every leg must be extendable',
        replicaBlock: 'A read-only DR replica follows its source — expand the source volume',
        shareBackedBlock:
          "This volume backs a file share — capacity is the share's gesture, not the volume's: grow it from the share's Resize (GROW-MS: legs, mirror and ext4 follow online)",
        submit: 'Expand'
      },

      // GUI-VERBI #34 (2026-08-29): the shrink — sudo + confirm-typed, the
      // R5 per-species doctrine said a priori (contract actionShrink).
      shrink: {
        action: 'Shrink',
        title: 'Shrink volume {name}',
        submit: 'Shrink',
        hint: 'The missing verse of the resize — sudo and the typed volume name arm it. The new size must be strictly smaller.',
        newSize: 'New size (GiB)',
        current: 'Current size: {size} GiB',
        tooBig: 'Must be strictly smaller than the current {size} GiB',
        confirmLabel: 'Type the volume name to confirm',
        confirmHint: 'Type {name} exactly — the confirm rides the request, the client never fills it in',
        declaredHint:
          "Declared volume: the shrink lowers the PROMISE synchronously — nothing touches any device, and the freed capacity returns to the cluster's admission headroom.",
        cephFsHint:
          "CephFS volume: online — the promise lowers under the AGGREGATE GUARD: the sum of the volume's shares' sizes must fit the new size (the refusal counts; resize the shares first).",
        objectQuotaHint:
          'Object volume: the pool quota drops with the task — never below the observed usage plus margin (the refusal counts).',
        offlineWarning:
          "OFFLINE branch (ext4 on MultiSCSI): the share's daemons STOP, the filesystem is checked and resized, then re-served — a DECLARED unavailability window, fsck-dominated. The dialog does not wait: the row's phase tells the truth.",
        lunBlock:
          "Block (LUN) shrink is outside the ratified v1 perimeter — the filesystem inside the LUN is the consumer's, and bytes beyond the new boundary would be cut (ratifica 2026-08-28)",
        xfsBlock: 'XFS cannot shrink, ever — the way is a new share and a copy',
        msObjectBlock:
          "Object store on MultiSCSI: shrink is a declared residue (the store rides the volume's ext4 — a later round)",
        replicaBlock: 'A read-only DR replica follows its source — shrink the source volume',
        snapshotWarning:
          'This volume has {count} snapshot(s): shrink is refused while snapshots exist (they pin the old size — ratifica 2026-08-28). Delete them first, or the server will answer 409.'
      },

      // GUI-VERBI DISK-2 #50 (2026-08-29): the CRUSH device class of the
      // volume's own pools — ceph only, counted against the OSD census.
      // ENEA HP-SAN-1 R19 (2026-10-24): the volume exported to an I/O server pair.
      ioServerPair: {
        label: 'I/O server pair',
        none: 'not exported to a pair',
        select: 'Export to an I/O server pair (optional)',
        selectNone: 'No pair',
        selectHint:
          'The volume is born exported to the pair (its pool joins the pair’s caps): a Lustre OST/MDT/MGT. The pair must be ready on the same Ceph cluster.',
        lustreRole: 'Lustre role',
        lustreRoleNone: 'No role',
        lustreIndex: 'Lustre index',
        roleHint:
          'Descriptive — the render, the CLI and the R20 runbook show it. An MDT/MGT must be replicated ×3 on the pair’s metadata device class (the server refuses otherwise); an OST takes any redundancy (EC 4+2 is the offer’s default).',
        holder: 'Holder (rbd lock)',
        holderNone: 'nobody holds the lock',
        watchers: 'Watchers',
        roles: { ost: 'OST (data)', mdt: 'MDT (metadata)', mgt: 'MGT (management)' }
      },
      deviceClass: {
        label: 'Device class',
        any: 'Any (no constraint)',
        free: 'Any CRUSH class name (R19): hdd, ssd, nvme, or a class of your own (e.g. nvme-meta) — empty = no constraint',
        hint: "CRUSH device class the volume's pools must ride (ceph only) — the admission counts the class's OSDs and refuses a class with too few, speaking.",
        option: '{class} — {count} OSD(s) observed',
        immutableHint:
          'Redundancy (device class included) is writable only while the volume is DECLARED — after materialization re-shaping live pools is a declared later round'
      },

      // M5d (2026-08-04): the per-volume QoS panel — BOTH ENGINES since
      // QoS-2 #36 (2026-08-26, docs/qos2-disegno-2026-08-26.md), with each
      // engine's enforcement honesty in its own hint (contract Volume.qos).
      qos: {
        tabTitle: 'QoS',
        summary: 'QoS',
        unlimited: 'Unlimited',
        iops: 'IOPS limit',
        iopsHint: 'Empty = unlimited on this axis',
        mbps: 'Bandwidth limit (MiB/s)',
        mbpsHint: 'Empty = unlimited on this axis',
        hint: "Limits are applied per volume and survive node failures (they live in the Ceph cluster). Enforcement, honestly: a hard token bucket honoured by native RBD consumers (librbd); an iSCSI-exported Ceph volume is NOT shaped — the gateway's krbd mapping does not evaluate librbd QoS.",
        hintMultiscsi:
          'Limits are applied per volume at the iSCSI gateway (a real token bucket, IOPS and bandwidth totals per axis) and follow the volume across failovers. Honestly: changing the values of an active QoS is live; enabling or disabling QoS on an exported volume briefly re-establishes the LUN (initiators with normal retry/MPIO semantics ride it out).',
        apply: 'Apply limits',
        clear: 'Remove limits',
        invalid: 'Limits are positive whole numbers',
        replicaBlock: 'A read-only DR replica exports nothing to shape',
        applied: 'Limits applied — the enforcement task is running'
      }
    },
    snapshots: {
      tabTitle: 'Snapshots',
      createTitle: 'Create snapshot',
      name: 'Name',
      nameHint: 'Lowercase letters, digits and dashes',
      stamp: 'Stamp',
      phase: 'Phase',
      phases: {
        pending: 'Pending',
        ready: 'Ready',
        error: 'Error',
        deleting: 'Deleting'
      },
      observed: 'Observed',
      states: {
        complete: 'Complete',
        partial: 'Partial',
        missing: 'Missing'
      },
      notObserved: 'Not yet observed',
      empty: 'No snapshots yet',
      notReady: 'Only Ready snapshots allow this action',
      volumeNotReady: 'Snapshots need a Ready volume — a degraded set would capture non-identical legs (contract 422)',
      clone: 'Clone into a new volume',
      cloneTitle: 'Clone snapshot {name}',
      cloneHint:
        'The clone is an independent volume on the same cluster and vdisks — deleting the snapshot (or the source volume) never invalidates it.',
      cloneName: 'New volume name',
      revert: 'Revert the volume to this snapshot',
      revertTitle: 'Revert to snapshot {name}',
      revertWarning:
        'The current content of volume {volume} will be REPLACED by this snapshot. Later snapshots stay available, but changes never snapshotted are lost.',
      revertConfirmLabel: 'Volume name',
      revertConfirmHint: 'Type {name} to confirm the revert',
      revertSubmit: 'Revert',

      // ENEA-A-R5 (2026-10-23): the FROZEN retention — one vocabulary for
      // the volume snapshots and the share's snapshot-resources.
      retention: {
        column: 'Retention',
        none: 'None',
        until: 'Until {date}',
        retainUntil: 'Retain until',
        retainUntilHint:
          'Optional. At least one minute and at most 100 years ahead. FROZEN once set: it can only be extended, never shortened or removed — not even by an administrator.',
        createHint:
          'A retention is a promise of the product: while it runs, the snapshot cannot be deleted and the data under it cannot be destroyed (the server answers 409 with the date). It does not fence the storage CLI underneath.',
        frozenHint: 'Frozen retention: delete and destruction are refused until this instant; it can only be extended.',
        deleteBlocked: 'Retention is running: the delete is refused until it ends (409 retention_active)',
        extend: 'Extend the retention',
        extendTitle: 'Extend retention of {name}',
        extendSubmit: 'Extend',
        extendHintActive:
          'The retention currently ends on {until}. Only a later instant is accepted — shortening or removing it is refused (422 retention_frozen).',
        extendHintNone: 'This snapshot has no retention. Setting one freezes it: from then on it can only be extended.'
      },

      // M4a — CHANGED BLOCK TRACKING (2026-08-04,
      // docs/cbt-snapdiff-design-2026-08-04.md): the Compare action. Not one
      // string names an engine — the answer is identical on both, and the only
      // engine-visible fact (the granularity) is a number in the summary.
      diff: {
        action: 'Compare',
        compareFrom: 'Compare from this snapshot',
        title: 'Compare snapshots of {name}',
        hint: 'Lists WHICH BLOCKS changed between two points in time, so a backup can copy the delta instead of re-reading the volume. It is a read: nothing is modified.',
        from: 'From (baseline)',
        fromHint: 'The older end — the point the backup already holds',
        to: 'To',
        toHint: 'The newer end: a later snapshot, or the live volume',
        head: 'Live volume',
        headHint:
          'Against the live volume the newer end is not a point in time: blocks may change while the diff runs, so the answer is everything that COULD have changed since the baseline.',
        run: 'Compare',
        running: 'Computing the changed blocks…',
        volumeNotReady: 'Only Ready or Degraded volumes can be compared',
        noBaseline: 'At least one Ready snapshot is needed as the baseline',
        noResult: 'The task ended without a readable result',
        granularity: 'Granularity',
        granularityHint:
          'The smallest unit the storage can mark as changed: the answer resolves no finer than this, and a backup rounds its reads to it.',
        extentCount: 'Changed extents',
        changedBytes: 'Changed bytes',
        changedBytesPartial:
          'At least this much: only the inline part of the list is loaded — load the full list for the exact total.',
        share: 'Share of the volume',
        extentsEmpty: 'Nothing changed between these two points',
        offset: 'Offset',
        length: 'Length',
        truncated: 'The result carries the first {inline} extents of {total}: the full list lives on the task log.',
        loadFull: 'Load the full list',
        showing: 'Showing {shown} of {total}',
        showMore: 'Show {count} more'
      }
    },
    hosts: {
      title: 'Hosts',
      createTitle: 'Create host',
      editTitle: 'Edit host {name}',
      edit: 'Edit name and IQNs',
      name: 'Name',
      nameHint: 'Lowercase letters, digits and dashes',
      iqns: 'Initiator IQNs',
      iqnHint: 'iqn.YYYY-MM.reverse.domain[:suffix], eui.<16 hex> or naa.<16-32 hex>',
      iqnDuplicate: 'This IQN is already listed on this host',
      addIqn: 'Add IQN',
      removeIqn: 'Remove IQN',
      chapFence:
        'Access control is by initiator IQN only — an IQN is an identity claim, not a secret. CHAP authentication arrives with the credentials round; keep consumers on a trusted data network.',
      removalWarning:
        'Removing an IQN drops its access on the next reconcile pass and logs that initiator out — it is a data-path action.'
    },
    targets: {
      // Menu/route label (dettatura Luca 2026-08-06): the section reads LUN —
      // the consumer-facing name; the resources inside stay targets.
      nav: 'LUN',
      title: 'Targets',
      createTitle: 'Create target',
      name: 'Name',
      nameHint: 'Lowercase letters, digits and dashes',
      volume: 'Volume',
      volumeHint: 'One target exports one volume at LUN 0 — only serving volumes without a target are listed',
      noVolumes: 'No exportable volume — it must be Ready or Degraded and not already exported.',
      protocol: 'Protocol',
      protocols: {
        iscsi: 'iSCSI',
        rbd: 'Native RBD'
      },
      protocolHint:
        'iSCSI serves both kinds (a Ceph volume rides the rbd-map gateway bridge); native RBD serves volumes on a Ceph cluster only',
      rbdNoVolumes: 'No exportable Ceph volume — native RBD serves volumes on a Ceph cluster only.',
      rbdHostsHint:
        'Each host gets its own platform-minted, pool/image-capped cephx identity; removing a host revokes it — a data-path action.',
      hosts: 'Hosts',
      hostsHint: "The ACL set is the union of the selected hosts' initiator IQNs",
      noAclWarning: 'No host selected: the target has no ACLs — no initiator can log in (a parked export).',
      noAcl: 'No ACL — no access',
      noHostsDefined: 'No host is defined yet — create one from the Hosts page to grant access.',
      iqn: 'Target IQN',
      edit: 'Edit name and hosts',
      editTitle: 'Edit target {name}',
      editWarning:
        'Removing a host logs its initiators out of the LUN — host changes are data-path actions, converged by the next reconcile pass.',
      deleteTitle: 'Delete target {name}?',
      deleteWarning:
        'Logged-in initiators lose the LUN — deleting the target is a data-path action. The export is torn down on every node.',
      tabTitle: 'Targets',
      tabOverview: 'Overview',
      tabExport: 'Export',
      empty: 'This volume is not exported by any target yet',
      manageHint: 'Targets are managed from the Storage → Targets page.',
      pathsTitle: 'Paths (per node)',
      pathsEmpty: 'No path observations yet',
      observedAt: 'Observed at',
      notObserved: 'Not yet observed',
      path: {
        node: 'Node',
        portal: 'Portal',
        alua: 'ALUA',
        observedAt: 'Observed at'
      },

      // M2g (2026-08-03, the keystore round): optional CHAP on the target's
      // ACLs — a reference to an iscsiChap keystore credential, never a secret.
      chap: 'CHAP',
      chapNone: 'None — ACL by IQN only',
      chapCredential: 'CHAP credential',
      chapCredentialHint: 'An iSCSI CHAP keystore credential — the secret is resolved at use time and never shown here',
      chapMutual: 'Mutual CHAP',
      chapMutualHint:
        'The target also authenticates itself to the initiator — needs a credential carrying the mutual pair',
      chapWarning:
        'Enabling, re-pointing or removing CHAP logs out initiators that do not match — a data-path action, converged by the next reconcile pass.',
      chapNoCredentials:
        'No iSCSI CHAP credential is listable — create one from the Credentials page (or ask for credential.read).',

      // M3 (2026-08-03, the Ceph engine round): the native RBD export render —
      // mount info (monitors/pool/image/per-host cephx identity), no secret.
      rbd: {
        title: 'RBD mount info',
        monitors: 'Monitors',
        pool: 'Pool',
        image: 'Image',
        clientsTitle: 'Client identities',
        client: {
          host: 'Host',
          entity: 'cephx entity',
          credential: 'Credential'
        },
        clientsEmpty: 'No host selected — no minted identity (a parked export)',
        noSecret: 'The cephx key never renders here: it lives write-only in the keystore and is resolved at use time.',
        notBuilt: 'Not yet built — the export task has not landed',
        copy: 'Copy',
        copied: 'Copied'
      }
    },

    // NAS-1 (2026-08-04, docs/nas-file-services-feasibility-2026-08-04.md +
    // contract fileshares.yaml): the File Shares page — ENGINE-NEUTRAL
    // NFS/SMB/SFTP shares. Not one string names an engine as the user's
    // choice: multiscsi/ceph is the cluster's detail, exactly like volumes.
    fileShares: {
      title: 'Filesystems',
      createTitle: 'Create file share',
      name: 'Name',
      nameHint:
        'Lowercase letters, digits and dashes — also the exported name (NFS path /name, SMB share name, SFTP label)',
      datapool: 'Cluster',
      sizeGiB: 'Size (GiB)',
      sizeImmutableHint:
        "Capacity never changes through PATCH — resizing is the share's own action (#34: cephfs both verses; a MultiSCSI share resizes through its volume's verbs)",
      used: 'Used',

      // GUI-VERBI #34 (2026-08-29): the resize action — cephfs both verses
      // (synchronous quota); MultiSCSI redirects to the volume's verbs.
      resize: {
        rowLabel: 'Size / resize',
        action: 'Resize',
        title: 'Resize share {name}',
        submit: 'Resize',
        hint: "Both verses: grow within the volume's promise, shrink above the observed usage plus margin — the refusals count the numbers.",
        growHint:
          "GROW: the aggregate of the volume's shares must keep fitting the volume's promise (the refusal counts — grow the volume first). The quota converges within a reconcile pass.",
        shrinkHint:
          "SHRINK: never below the share's OBSERVED usage plus margin (ceph accepts a quota below the used bytes and the share goes over-full: writes block — hence the floor; an unobserved share refuses honestly).",
        newSize: 'New size (GiB)',
        current: 'Current size: {size} GiB',
        sameSize: 'Must differ from the current {size} GiB',
        msGrowHint:
          "GROW (MultiSCSI): the share IS its volume — this grows the backing volume's legs and mirror (re-admitted against the cluster's observed capacity) and then the ext4 ONLINE, while the share keeps serving. Snapshots are allowed. A smaller size is the volume's shrink, not this dialog.",
        msShrinkViaVolume:
          "Shrink of a MultiSCSI share rides the volume's sudo confirm-typed action (the ext4 offline window)",
        engineUnknown: "The share's engine is not readable right now (cluster catalog degraded) — retry once it is",
        volumeLink: 'open the backing volume'
      },
      protocols: 'Protocols',
      protocolsHint: 'Pick at least one card — each protocol opens its own config panel below',
      protocolNames: {
        nfs: 'NFS',
        smb: 'SMB',
        sftp: 'SFTP',
        ftp: 'FTP',
        tftp: 'TFTP',
        svcgssd: 'GSS (Kerberos NFS)'
      },

      // SHARE-1PROTO (#213, 2026-08-24): the create is a CARD LIST split in
      // two tabs — the tab fixes the (immutable) permission model, each tab
      // shows ONLY the protocols legal for that model (nt → smb; posix →
      // nfs/sftp/ftp/tftp). One permission model, one protocol group.
      modelTabs: {
        posix: 'POSIX ACLs',
        nt: 'Rich NT ACLs'
      },
      protocolCards: {
        nfs: 'The Unix/Linux network filesystem — host allow-lists (AUTH_SYS) or Kerberos on an AD realm.',
        smb: 'The Windows share — rich NT ACLs are the authority: permissions managed from Explorer are found identical.',
        sftp: 'File transfer over SSH — chrooted to the share, file users with passwords or keys.',
        ftp: 'FTPS transfer (explicit TLS) with a declared passive port range.',
        tftp: 'The PXE/netboot tree — unauthenticated, read-only by default, MultiSCSI only.'
      },
      ntExplorerHole:
        'The web explorer on an NT share applies the kiosk perimeter (user lists + writable), NOT the per-file NT ACLs — DACL enforcement on the web plane is a dedicated, declared round.',
      serviceAddress: 'Service address',
      serviceAddressHint:
        "The IPv4/IPv6 address clients connect to — the platform assigns it to the authority node's data interface and moves it on failover (gratuitous ARP)",
      serviceAddressCeph:
        "No floating address on a Ceph share in this round: clients connect to the member nodes' own data addresses, active/active.",
      serviceAddressOn: 'Address held by',
      phase: 'Phase',
      phases: {
        pending: 'Pending',
        creating: 'Creating',
        ready: 'Ready',
        degraded: 'Degraded',
        error: 'Error',
        deleting: 'Deleting'
      },
      tabOverview: 'Overview',
      tabExports: 'Exports',
      tabClients: 'Clients',
      tabQuota: 'Quota',
      mountedOn: 'Mount node',
      cephActiveActive: 'Every member node (active/active for NFS, SFTP and FTP; SMB is served by one owning gateway)',
      path: 'Path',
      volume: 'Volume',
      capacity: 'Capacity',
      free: 'free',
      observedAt: 'Observed at',
      notObserved: 'Not yet observed',

      // #189 GUI-RESIDUI: the filesystem the share was born with — recorded
      // at admission, never rewritten. `cephfs` is the null render (CephFS IS
      // the filesystem), not a contract enum value.
      fsType: 'Filesystem',
      fsTypes: {
        xfs: 'XFS',
        ext4: 'ext4',
        cephfs: 'CephFS'
      },

      // #111 (#189): the per-folder quota block + its observation.
      quota: {
        createLabel: 'Root quota (MiB)',
        createHint:
          'Optional: a limit on the share root, tighter than the size. Tighten, retarget or remove it later from the Quota tab.',
        multiscsiHint:
          'Project quotas on the mounted filesystem: the kernel counts at the instant of the write, so limits stop EXACTLY, anywhere in the tree, and are inherited by subdirectories created later.',
        cephLateHint:
          'On a Ceph share the limit lives on the SUBVOLUME ROOT and is an alarm that arrives late, not a wall: writes may keep landing for a few seconds past it (the MDS propagates the usage statistic with a delay — measured 7-8 s idle). No tolerance is promised on purpose; size the limit accordingly.',
        scope: 'Quota',
        scopeNone: 'No quota',
        scopes: {
          share: 'Share root only',
          subtree: 'Per-directory limits (subtree)'
        },
        cephSubtreeBlock:
          'refused on a Ceph share: inside a subfolder the limit would not be late, it would be ABSENT (422 quota_scope_not_supported)',
        rootLimit: 'Root limit (MiB)',
        rootLimitRequired:
          'Required — a scope with no number declares nothing. Min 1 MiB, never above the share size (a quota tightens, it never expands).',
        rootLimitOptional:
          'Optional with per-directory limits: empty = no ceiling on the root, only on the listed directories.',
        dirsTitle: 'Directory limits',
        dirsHint:
          'Each entry is a project-quota realm: the directory is created if missing, and the limit is inherited by everything created under it. Paths are relative to the share root; realms may not nest.',
        dirPath: 'Directory',
        dirLimit: 'Limit (MiB)',
        dirAdd: 'Add directory',
        dirDuplicate: 'The same directory is listed twice',
        dirNested:
          'One realm nests inside another — two overlapping limits on one tree would make "which limit bit" unanswerable',
        saved: 'Quota saved — a quota is pure config and converges on the reconcile pass (~30 s).',
        usageTitle: 'Usage (as observed)',
        usageRoot: 'share root',
        usageUsed: 'Used',
        usageLimit: 'Limit in force',
        usageObservedAt: 'Observed at',
        usageNotApplied: 'not applied yet',
        usageNoQuota: 'No quota block — nothing to observe (absent means NOT OBSERVED, never an invented zero).',
        usageNotObserved: 'The serving node has not reported the quota usage yet.'
      },
      identity: {
        label: 'Identity source',
        localHint:
          'Local identities (the NAS-1 behavior): NFS trusts the allow-list (AUTH_SYS), SMB/SFTP/FTP authenticate local file users. IMMUTABLE after creation — moving to a directory later means a new share plus a copy.',
        directoryHint:
          'The share takes identities from this realm — SMB admits its domain principals, and on an AD realm NFS may demand Kerberos. IMMUTABLE after creation (new share + copy is the way back).',
        modes: {
          local: 'Local',
          directory: 'Directory'
        }
      },
      aclModel: 'Permission model',
      aclModels: {
        posix: 'POSIX ACLs',
        nt: 'NT ACLs (rich)'
      },
      aclModelHints: {
        posix:
          'POSIX ACLs are the authority. This model carries NFS, SFTP, FTP and TFTP — never SMB (SHARE-1PROTO #213: one permission model, one protocol group). Immutable at birth.',
        nt: 'The NT descriptor is the authority — a Windows administrator manages permissions from Explorer and finds them identical. This model carries ONLY SMB: protocols that cannot read NT ACLs never ride it. Immutable at birth.'
      },
      smbLdapBlock:
        'SMB cannot ride an LDAP realm: no join, no machine account, no NT hash to authenticate with (ratifica B1). Use an Active Directory realm, or the other protocols.',
      smbOwnerChip: 'SMB owner: {node}',

      // #156 (#181 ondata 4, ratifica 2026-08-19 §5): the SMB lease the
      // leader could NOT renew — alarm tone, because the phase alone reads
      // healthy while arbitration is gone.
      smbLeaseChip: 'SMB LEASE NOT RENEWED',
      smbLeaseUnrenewed:
        'Served WITHOUT arbitration since {since}: the gateway keeps serving this SMB share with a lease it could not renew because the cluster store did not answer. Serving is deliberate (stopping would black out SMB on every gateway at once) — but from that instant another gateway may take the lease and serve the same share, and byte-range locks are no longer refereed. The alertable gauge is share.smbLeaseUnrenewedSeconds.',
      principals: {
        title: 'Domain principals of realm {realm}',
        entry: 'Principal',
        hint: "DOM\\user for a person, @DOM\\group for a group — DOM is the realm's NetBIOS name. They go straight into smb.conf valid users; whether a principal EXISTS is the realm preflight's question to the domain controller.",
        duplicate: 'This principal is already listed',
        add: 'Add principal',
        remove: 'Remove principal'
      },
      create: {
        datapoolHint: 'Only storage clusters in the Ready or Degraded phase accept new shares',
        noDatapools: 'No storage cluster is serving — create one and let its fabric initialize first.',
        volumeHint:
          "The declared volume this share consumes — on Ceph the volume's own CephFS takes several shares within its promise; on MultiSCSI one share is the volume's whole filesystem",
        noVolumes:
          "No consumable volume — declare one from the Volumes page first (a declared volume is free; the share's creation materializes it as a filesystem).",
        sizeLockedHint: "On MultiSCSI the share's size IS the volume's declared size",
        cephHint:
          "On a Ceph cluster the share is a CephFS subvolume exported from every member node — active/active for NFS, SFTP and FTP, while SMB is served by exactly one owning gateway (its byte-range locks do not cross gateways, NFS's do). Durability is the pool's profile, so replicas and service address do not apply.",
        multiscsiHint:
          'On a MultiSCSI cluster the share owns a mirror volume, formatted and mounted by its authority node — one mounter by construction; mount, daemons and service address follow the authority on failover.'
      },
      nfs: {
        title: 'NFS',
        allow: 'Allow-list',
        allowHint:
          "IPv4/IPv6 addresses, CIDR subnets (10.1.2.0/24) or * — the whole NFS access model here: the client's claimed identity is trusted inside this list",
        allowEntry: 'Host or subnet',
        addEntry: 'Add entry',
        removeEntry: 'Remove entry',
        duplicate: 'This entry is already listed',
        squash: 'Root squash',
        squashRoot: 'Squash root (safe default)',
        squashNone: 'No root squash (trusted appliance mounts)',
        readOnly: 'Export read-only',
        sec: 'Security flavor',
        secHint:
          "krb5 authenticates every user with a Kerberos ticket against the share's AD realm; krb5i adds integrity, krb5p encryption on the wire. A client asking a different flavor is refused — never a silent downgrade.",
        secGate:
          'AUTH_SYS trusts the uid the client claims, inside the allow-list — the honest posture without a domain. The krb5* flavors need this share bound to an Active Directory realm (no realm = no machine account = no nfs/ keytab).',
        secOptions: {
          sys: 'AUTH_SYS (trust the client)',
          krb5: 'Kerberos (krb5)',
          krb5i: 'Kerberos + integrity (krb5i)',
          krb5p: 'Kerberos + privacy (krb5p)'
        }
      },
      smb: {
        title: 'SMB',
        users: 'Local users',
        usersHint:
          'File-user keystore credentials — the referenced user must carry a password; there is never guest access',
        noUsers:
          'No file-user credential is listable — create one from the Credentials page (or ask for credential.read).',
        readOnly: 'Export read-only',
        encryption: 'SMB3 encryption',
        encryptionHint:
          'server smb encrypt of this share: desired encrypts with every client that can (the product default), required refuses unencrypted sessions (and makes signing mandatory on the gateway), off never encrypts',
        encryptionChip: 'encryption: {mode}',
        encryptionOptions: {
          desired: 'desired',
          required: 'required',
          off: 'off'
        }
      },
      sftp: {
        title: 'SFTP',
        users: 'Users',
        usersHint:
          'The same file-user credentials — password and/or SSH public keys; a chrooted SFTP-only instance on its own port, never the management sshd',
        readOnly: 'Export read-only'
      },
      exports: {
        readOnly: 'read-only',
        readWrite: 'read-write',
        squashNone: 'no_root_squash',
        exportName: 'Export name',

        // #185 (FS-PATCH, 2026-08-19): the post-creation editors — principals,
        // NFS security flavor and previous versions are writable after birth
        // under the same admission rules as at create; identity and the
        // permission model stay immutable (new share + copy is the way out).
        editTitle: 'Post-creation changes',
        editHint:
          'Domain principals, the NFS security flavor and Previous versions are writable after creation under the SAME rules as at birth — pure config, converging on the reconcile pass (~30 s). The identity source and the permission model stay immutable: changing them means a new share plus a copy.',
        save: 'Save export changes',
        saved: 'Saved — export changes are pure config and converge on the reconcile pass (~30 s).'
      },

      // ENEA-A-R2 (2026-10-23): the NFS engine chip on a ceph gateway's nfs daemon row.
      nfsEngine: {
        ganesha: 'Ganesha',
        knfsd: 'kernel NFS (knfsd)'
      },
      clients: {
        total: 'Connected clients',

        // ENEA-B-R15 (2026-10-24): the floating gateway addresses the share is announced on.
        tenantAddresses: 'Tenant gateway addresses',
        tenantAddressesHint:
          "The floating gateway addresses this share is announced on with their current holder: the tenant's own when its scope has assigned addresses, the cluster's common ones otherwise. The separation is per address and per client list/authentication, not per bind nor firewall.",
        daemonsTitle: 'Daemons',
        empty: 'No daemon observations yet',
        node: 'Node',
        protocol: 'Protocol',
        state: 'State',
        pid: 'PID',
        connections: 'Clients',
        observedAt: 'Observed at'
      },
      remount: {
        action: 'Remount',
        title: 'Remount share {name}',
        hint: "Re-renders every config, remounts the filesystem and restarts the share's daemons — the repair verb when a daemon wedged or an export never converged. NFS hard mounts and SMB reconnection ride out the brief stall.",
        submit: 'Remount',
        busyBlock: 'The share is being created or deleted',
        lockdownBlock:
          'The share is in lockdown — the contract refuses a remount on a locked share; lift the lockdown first (the unlock action here, sudo)'
      },

      // #181 T11 (SEC-REVOKE D5 #79): the kill-switch state, said on the row —
      // and since ondata 1c the ACTIONS live here too (lockdown/unlock below).
      lockdownChip: 'LOCKDOWN',
      lockdownHint:
        'In lockdown since {at} by {by} — serving stopped on every protocol, files-realm sessions revoked, editor tokens destroyed. Explorer and editor answer 409 until unlock (the action on this row, sudo).',

      // #181 ondata 1c (SEC-REVOKE D5 #79): the kill-switch pair. The scale is
      // said BEFORE the click — dictation 3C: the WHOLE session of every
      // fileUser the share names falls, on every share it reached — and the
      // result phase renders what the server says fell (counters, lockedAt/By).
      // ENEA-A-R9 «replica file» (2026-10-23): the mirror REPLICA share —
      // what this cluster holds as the destination of another cluster's
      // file link; read-only until promoted.
      replica: {
        chip: 'Mirror replica',
        chipHint:
          'Read-only replica from cluster {source} — last synced snapshot: {snapshot}. Promote it to make it an ordinary share.',
        noneObserved: 'none observed yet',
        block: 'A mirror replica is read-only until promoted — the source’s mirror daemon writes it.',
        promote: {
          action: 'Promote the replica',
          title: 'Promote replica {name}?',
          point:
            'Replica from {source}: it stands at snapshot {snapshot} (taken {at}, ~{lag} s behind now). Everything the source wrote AFTER that point is LOST by this promotion — declared, not implied.',
          scale:
            'THE CUT: the peer’s cephx user on this cluster is revoked, so a late sync can never overwrite the promoted share — and the mirror of EVERY share riding the same peer stops. What reached this cluster is the snapshots’ data, not the source’s snapshot resources nor their retention. No automatic failback: the reverse protection is a new link from this cluster.',
          acknowledge: 'Acknowledge the recovery point',
          acknowledgeHint: 'Type the snapshot name exactly: {snapshot}',
          acknowledgeNoneHint:
            'No synced snapshot was ever observed on this replica — type none to acknowledge that the promotion may hold nothing.',
          confirm: 'Type the share name ({name}) to confirm',
          name: 'Promoted name (optional)',
          nameHint: 'Replaces the platform repl-… name; refused if taken in the scope.',
          submit: 'Promote now',
          done: 'Promotion admitted (task {task}): the peer user is revoked, the quota re-applied, the share becomes ordinary — give it protocols and exports.'
        }
      },
      lockdown: {
        action: 'Lockdown (kill-switch)',
        title: 'Lock down share {name}?',
        scale:
          'THE KILL-SWITCH: the share stops serving on EVERY protocol within one reconcile beat (~30 s) — connected clients lose it. Its document-editor tokens are destroyed NOW: unsaved work in an open editor is lost. Stopping a serving share is an outage by design; the press lands in the audit registry.',
        sessionsNote:
          'The whole scale: every files-realm session whose perimeter contains this share is revoked — the ENTIRE session of each fileUser the share names, on every share it reached, until a new login. Safe to press twice: a second press keeps the same lockdown instant and re-sweeps.',
        submit: 'Lock down now',
        busyBlock: 'The share has not finished its birth, or is being deleted — those windows belong to their tasks',
        done: 'In lockdown since {at} by {by} — {sessions} session(s) revoked, {tokens} editor token(s) destroyed.',
        doneBeat:
          'Serving stops on every protocol within one reconcile beat (~30 s); a concurrent reconcile can never re-open it. The unlock action lifts the lockdown — the revoked sessions and tokens stay revoked either way.'
      },
      unlock: {
        action: 'Lift the lockdown',
        title: 'Lift the lockdown on {name}?',
        hint: 'The share serves again on the next reconcile beat (~30 s). NOTHING ELSE is restored on purpose: the sessions and editor tokens the lockdown revoked STAY revoked — users log in again, editors re-mint. Re-opening a share an incident closed is a decision with a name on it (sudo, audited).',
        submit: 'Lift the lockdown'
      },
      delete: {
        title: 'Delete file share {name}?',
        warning:
          'The daemons stop and connected clients lose the share; the service address is released and the filesystem unmounted.',
        destroyLabel: 'Destroy the backing data too',
        multiscsiNote:
          "On MultiSCSI the share's bytes ARE the consumed volume's one filesystem: this delete unserves and keeps them (the volume survives, re-consumable). Destroying the bytes is the VOLUME's own delete, on the Volumes page.",
        keepHint:
          'Unchecked, the bytes survive: on MultiSCSI the backing volume lives on as an ordinary volume (filesystem intact); on Ceph the subvolume stays in the cluster filesystem, unmanaged.',
        destroyWarning:
          'Every byte of the share is destroyed with it — the backing volume (or CephFS subvolume) and its snapshots included. There is no undo.'
      },

      // NAS-3 (2026-08-05, PROPOSAL pending ratification): FTP(S), TFTP and
      // the PXE configuration block on the same share.
      ftp: {
        title: 'FTP (FTPS)',
        usersHint:
          'The same file-user credentials — FTP authenticates by password only, so the referenced user MUST carry one; sessions are chroot-jailed to the share',
        passiveFrom: 'Passive ports from',
        passiveTo: 'Passive ports to',
        passiveHint:
          'The declared FTPS passive data range (1024-65535): open exactly these ports on the firewall toward the service address. Reserved cluster-wide — an overlap with another share is refused.',
        plainFtp: 'Allow plain FTP (no TLS)',
        plainFtpWarning:
          'Plain FTP sends credentials and data in cleartext — a discouraged opt-out for legacy clients. Default is explicit FTPS (AUTH TLS on port 21).',
        readOnly: 'Export read-only'
      },
      tftp: {
        title: 'TFTP',
        hint: 'TFTP is unauthenticated by nature: no users, ever — access control is the network, and the export is READ-ONLY unless explicitly opted out. The PXE building block.',
        subpath: 'Served directory',
        subpathHint: 'Relative to the share root (no leading /, no ..) — empty serves the share root itself',
        writable: 'Accept uploads (firmware-dump territory)',
        cephBlock:
          'TFTP is MultiSCSI-only in this round: the PXE next-server needs the one floating service address Ceph shares do not carry.'
      },
      tabBoot: 'Boot / PXE',
      boot: {
        none: 'No TFTP on this share — add the tftp protocol to build a boot tree (PXE rides it).',
        noPxe:
          'TFTP is served, but no PXE menu is configured — set the pxe block via the API or CLI (patch-share --pxe-entries).',
        tftpRoot: 'TFTP root',
        shareRoot: 'the share root',
        access: 'Access',
        readOnly: 'read-only (default — unauthenticated protocol)',
        writable: 'uploads accepted',
        defaultEntry: 'Default entry',
        timeout: 'Menu timeout',
        timeoutForever: 'Wait forever',
        seconds: 'seconds',
        entries: 'Boot menu',
        entryName: 'Name',
        entryLabel: 'Label',
        entryKernel: 'Kernel',
        entryInitrd: 'Initrd',
        entryAppend: 'Append',
        artifactsHint:
          'The platform generates TWO menu files from the same entries: pxelinux.cfg/default (BIOS, syslinux syntax) and grub.cfg (UEFI, read by grubx64.efi — under Secure Boot upload the distro-signed shim+grubx64 pair). Loader binaries, kernels, initrds and images are files YOU upload onto the share (SMB/SFTP/FTP) — a menu entry pointing at a missing file fails at the booting client.',

        // #189 GUI-RESIDUI: the editor — the read-only seam closed.
        configure: 'Configure PXE menu',
        edit: 'Edit menu',
        defaultEntryHint: 'Booted when the timeout expires — empty picks the first entry',
        defaultFirst: 'First entry',
        timeoutHint: '0 = wait forever for an operator choice; empty = the default (10 s)',
        pathHint: 'Relative to the TFTP root — an artifact YOU uploaded; the platform never verifies it',
        addEntry: 'Add entry',
        duplicateNames: 'Entry names must be unique within the menu',
        removeMenu: 'Remove the PXE menu (TFTP keeps serving — TFTP without PXE is just TFTP)'
      },
      previousVersions: {
        title: 'Previous versions',
        hint: 'The Windows "Previous versions" tab, served from CephFS subvolume snapshots (vfs_shadow_copy2). Ceph only — ON by default there (the default follows the real cost of the engine, ratifica B5). Turning it off keeps the versions already taken: nothing silently destroys data.',
        choice: 'Snapshots',
        choiceDefault: 'Engine default (on — hourly, keep 24)',
        choiceCustom: 'Custom cadence',
        choiceOff: 'Off',
        interval: 'Interval (minutes)',
        intervalHint:
          '5 to 10080 — the cadence lives in the snapshot names, so it survives restarts without double-firing',
        keep: 'Keep',
        keepHint:
          '1 to 64 versions — the pruning removes the oldest beyond this many; operator-made snapshots are never counted nor pruned',
        on: 'On — every {interval} min, keep {keep}',
        off: 'Off — versions already taken stay on the subvolume',
        engineDefault: 'Engine default (on — hourly, keep 24)',
        notSupported:
          'Not available on a MultiSCSI cluster (every exposed snapshot there would be an assembled array and a mount of its own — declared seam)'
      },
      tabAcl: 'ACL',

      // ENEA-A-R5 (2026-10-23): the share's snapshot-resources.
      tabSnapshots: 'Snapshots',

      // ENEA-A-R12 (2026-10-23): the share's client audit — switch, closed
      // list of operations, the tail of the owner node's event file.
      // ENEA-B-R14 (2026-10-24): the share's QoS (throughput per service address; IOPS are the volume's).
      tabQos: 'QoS',
      qos: {
        intro:
          'Throughput ceilings of the share, opt-in (empty = no limit, floor 1 MiB/s): enforced as traffic shaping on the share’s SERVICE ADDRESS at the gateway (a leaf of the network QoS tree under the NAS class) — reads shaped, writes policed — so every protocol of the share passes through it and the ceiling follows the address on failover. IOPS are not a share knob: the consumed block volume limits them (set-volume-qos) and the share inherits them.',
        offerPhrase:
          'What the console and the collaudo say: throughput QoS per service address (tenant/gateway) and per share on multiscsi datapools; IOPS and throughput QoS per block volume; S3 ops/bytes QoS per account and per bucket on RGW; no per-share IOPS limit on CephFS.',
        cephHint:
          'A Ceph share has no service address of its own — the floating gateway addresses belong to the cluster and every gateway serves every share from every address, so no per-share ceiling can be told apart on the wire (the server answers 422 qos_not_supported). Set the ceiling per gateway address in the Ceph cluster card (gatewayQos); IOPS per share are not applicable on CephFS.',
        ceilings: 'Ceilings',
        readBps: 'Read ceiling (MiB/s)',
        writeBps: 'Write ceiling (MiB/s)',
        fieldsHint:
          'MiB/s (50, 1.5G, 2048K…); empty removes the ceiling on that axis. Reads are shaped (HTB), writes are policed (a drop ceiling, not a queue). Precision measured, not promised: −5 % at 2 and 8 MiB/s on loopback.',
        save: 'Save ceilings',
        saved: 'Ceilings saved — the gateway converges on its next pass.',
        current: 'Current',
        refusals: {
          notSupportedCeph: 'Not applicable to a Ceph share: use the cluster’s gatewayQos (per gateway address).',
          notSupportedBidirectional:
            'Not applicable to a bidirectional store: it serves from the cluster’s gateway addresses — use the cluster’s gatewayQos.',
          notSupportedBucketEngine: 'Bucket QoS is an RGW rate limit — this engine has none.',
          opsNotSupported:
            'Operations per second are not a ceiling here: IOPS are the volume’s QoS (set-volume-qos); versitygw has no rate limiting.',
          invalidBps: 'Not a throughput: use MiB/s (50, 1.5G, 2048K).',
          invalidOps: 'Not a count of operations per second (whole number).',
          floorBps: 'Below the floor of 1 MiB/s.',
          floorOps: 'Below the floor of 1 op/s.'
        },
        volumeQos: 'Inherited volume QoS (IOPS / MiB/s)',
        volumeQosNone: 'none — the consumed volume has no QoS',
        volumeQosCeph: 'not applicable on CephFS (no per-share IOPS limit — mClock is per cluster)',
        volumeQosHint:
          'The IOPS half of the share’s QoS lives on the consumed block volume (QoS-2): change it with set-volume-qos or in the volume card.',
        status: {
          title: 'Applied on the node',
          none: 'not reported yet',
          shaped: 'shaped on',
          notShaped: 'NOT shaped on',
          rgwApplied: 'applied per account',
          rgwNotApplied: 'not applied by the gateway',
          observedAt: 'Observed',
          hint: 'The status is what the node read back from the kernel (tc) or the daemon — never the wish: “not shaped” carries the reason (network QoS disabled on the device, device not derivable, tc missing).'
        }
      },
      tabAudit: 'Audit',
      audit: {
        intro:
          'The client audit records the operations clients perform on the share — protocol, source, user, client address, operation, path (never content), outcome, bytes — in ONE event form: SMB through vfs_full_audit, WebDAV through the gate, NFS on knfsd through fanotify on the exported path, NFS on Ganesha only mount/unmount and authentication events (user-space FSAL: no per-file audit). Events land in the owner node’s audit file, rotate on their own cap, reach every syslog sink that forwards daemon logs and feed the share.* audit metrics and the factory rules.',
        policy: {
          title: 'Policy',
          enabled: 'Client audit',
          on: 'On',
          off: 'Off (default)',
          operations: 'Operations recorded',
          operationsHint:
            'None selected = every operation. Failed connections/authentications/mounts and denied operations are recorded regardless (security).',
          costHint:
            'Off by default: vfs_full_audit costs per SMB operation and fanotify per NFS access — switch it on where it pays. With the audit on over a busy share the syslog collector is where the stream lives; the local file is a queue.',
          save: 'Save policy',
          saved:
            'Audit policy saved — the serving nodes converge on their next pass (the share’s smbd restarts on the change)'
        },
        operations: {
          connect: 'Connect',
          mount: 'Mount',
          auth: 'Authentication',
          open: 'Open',
          read: 'Read',
          write: 'Write',
          create: 'Create',
          delete: 'Delete',
          rename: 'Rename',
          mkdir: 'Make directory',
          rmdir: 'Remove directory',
          setattr: 'Set attributes',
          setacl: 'Set ACL',
          list: 'List',
          lock: 'Lock'
        },
        tail: {
          title: 'Last events',
          size: 'Events',
          refresh: 'Refresh',
          empty: 'No event yet — the audit is off, or no client operated since it was switched on',
          truncated: 'More events than shown match — the complete stream is on the syslog collector',
          footer: '{count} event(s) read from the file of {node}: {file}',
          time: 'Time',
          protocol: 'Protocol / source',
          user: 'User',
          client: 'Client',
          operation: 'Operation',
          outcome: 'Outcome',
          path: 'Path',
          bytes: 'Bytes',
          security: 'security',
          unavailable:
            'The owner node did not answer the tail read — the events live in its local file; retry, or read the syslog collector',
          forbidden: 'Reading the audit tail needs the audit.read permission on this resource'
        },
        outcomes: {
          ok: 'ok',
          denied: 'denied',
          error: 'error'
        }
      },

      // ENEA-A-R10 (2026-10-23): the share's file-metadata catalog.
      tabCatalog: 'Catalog',
      catalog: {
        intro:
          'The catalog is a PostgreSQL database of every entry on the share (path, type, size, owner, times, extension), filled by a crawler on the share’s owner node and opened three ways: this tab, the CSV/JSONL export and a read-only SQL role for your own SQL/BI client. Opt-in per share: a full scan reads every metadata of the share and the database grows on the owner node.',
        policy: {
          title: 'Policy',
          enabled: 'Catalog',
          on: 'On',
          off: 'Off',
          everyMinutes: 'Every (minutes)',
          mode: 'Mode',
          modes: { auto: 'Auto', full: 'Full', incremental: 'Incremental (rctime)' },
          incrementalBlock: 'CephFS only',
          fullEvery: 'Full scan every N scans',
          maxEntries: 'Max entries',
          maxEntriesHint: 'Empty = no cap (≈ 450–600 bytes per entry on the owner node)',
          save: 'Save policy',
          saved: 'Policy saved — the owner node’s worker converges on its next tick (the first scan is full).',
          hint: 'Auto = incremental by CephFS recursive ctime where available, full elsewhere; the first scan is always full, and one full scan every N keeps the incremental heuristic honest. Switching the catalog off keeps the rows already catalogued.'
        },
        status: {
          title: 'Status',
          owner: 'Owner node',
          database: 'Database',
          notBorn: 'not born yet — the owner node’s worker creates it on its first tick with the catalog enabled',
          totals: 'Totals',
          totalsLine: '{entries} entries · {files} files · {dirs} directories · {bytes}',
          none: 'no finished scan yet',
          lastScan: 'Last scan',
          scanLine: '{entries} entries · {errors} errors · {skipped} skipped subtrees · {removed} removed',
          runningLine: 'A {mode} scan is running since {at}',
          nextDue: 'Next scan',
          due: {
            disabled: 'Off',
            notBorn: 'Waiting for the database',
            running: 'Scanning',
            due: 'Due',
            scheduled: 'Scheduled',
            neverScanned: 'Never scanned'
          }
        },
        scan: {
          action: 'Scan now',
          started: 'Scan task {task} started on the owner node.',
          disabled: 'Switch the catalog on first',
          running: 'A scan is already running'
        },
        outcome: { ok: 'ok', partial: 'partial', failed: 'failed', cancelled: 'cancelled', running: 'running' },
        modeLabel: { full: 'full', incremental: 'incremental' },
        kinds: { file: 'file', dir: 'directory', symlink: 'symlink', other: 'other' },
        filters: {
          title: 'Entries',
          pathPrefix: 'Under directory',
          extension: 'Extension',
          type: 'Type',
          anyType: 'Any',
          sizeMin: 'Min size (MiB)',
          sizeMax: 'Max size (MiB)',
          mtimeFrom: 'Modified from',
          mtimeTo: 'Modified to',
          nameContains: 'Name contains',
          sort: 'Sort',
          apply: 'Apply',
          reset: 'Reset',
          page: 'Page {n}',
          firstPage: 'First page',
          next: 'Next page'
        },
        columns: { path: 'Path', type: 'Type', size: 'Size', mtime: 'Modified', owner: 'uid:gid', mode: 'Mode' },
        empty: 'No entry matches these filters.',
        summary: {
          title: 'Breakdown',
          byExtension: 'By extension',
          byOwner: 'By owner',
          byDirectory: 'By top-level directory (files · subdirectories · bytes)',
          noExtension: '(none)'
        },
        export: {
          csv: 'Export CSV',
          jsonl: 'Export JSONL',
          done: '{name} downloaded (the current filters and sort apply).'
        },
        credentials: {
          action: 'SQL credentials',
          title: 'Read-only SQL credentials',
          intro:
            'One role per catalog database, READ-ONLY: it reads the catalog of EVERY share of this owner node and nothing else. Your SQL/BI client reaches the owner node’s PostgreSQL from the management network — TLS and a pg_hba rule for this role are the deployment’s part (manual §6.11).',
          reveal: 'Reveal',
          tls: 'TLS offered — certificate sha256 {fp}',
          tlsNone:
            'The server does not offer TLS today: configure it before exposing this port beyond the management network.'
        }
      },
      snapshots: {
        intro:
          'Point-in-time snapshots of the share, named by you and visible to clients under the .snap directory (kernel NFS and CephFS mounts; see the manual for Ganesha). Each may carry a frozen retention. The scheduled Previous Versions snapshots are a separate family and are not listed here.',
        create: 'Create snapshot',
        createTitle: 'Snapshot of share {name}',
        name: 'Name',
        nameHint: 'Lowercase letters, digits and dashes — unique within the share',
        cephName: 'Cluster name',
        empty: 'No snapshots yet',
        notReady: 'Only Ready snapshots allow this action',
        shareNotReady: 'The share must be Ready before a snapshot can be taken',
        multiscsiBlock:
          'A MultiSCSI share has no snapshots of its own: the share IS the volume there — take the snapshot on the volume (Volumes → Snapshots).',
        clone: 'Clone into a new share',
        cloneTitle: 'Clone snapshot {name}',
        cloneHint:
          "The clone is a NEW, independent share on the same volume: its own data copied from the snapshot, the source share's configuration (protocols, users, ACL model) carried over. Deleting the snapshot afterwards never invalidates it; only while the clone is being born is the snapshot's delete refused.",
        cloneName: 'New share name',
        cloneSize: 'Size (GiB)',
        cloneSizeHint: "The new share's own quota on the volume; whole GiB",
        deleteBody:
          'The snapshot and its point-in-time data are removed (a task runs it). A snapshot under retention or with a clone still being born is refused by the server, with the reason.'
      },
      acl: {
        path: 'Path',
        pathHint: 'Relative to the share root — empty is the root itself',
        load: 'Load ACL',
        loading: 'Reading the ACL from the serving node…',
        lossyForChip: '{protocol} sees a lossy view',
        daclAbsent:
          'No rich ACL has been written on this path yet: absent is NOT empty — effective permissions are the POSIX bits underneath (an empty ACL would mean nobody-nothing).',
        owner: 'Owner (SID)',
        group: 'Group (SID)',
        ownerHint: 'Empty = leave as is',
        kind: 'Kind',
        kinds: {
          allow: 'Allow',
          deny: 'Deny',
          audit: 'Audit (SACL)'
        },
        trustee: 'Trustee',
        trusteeHintNt: 'A SID (S-1-5-21-…) — the name that survives a user rename and a domain change',
        trusteeHintPosix: 'owner@, group@, everyone@, mask@, user:<uid> or group:<gid>',
        rightsLabel: 'Rights',
        rights: {
          readData: 'Read data',
          writeData: 'Write data',
          appendData: 'Append',
          readEa: 'Read EAs',
          writeEa: 'Write EAs',
          execute: 'Execute',
          deleteChild: 'Delete child',
          readAttributes: 'Read attrs',
          writeAttributes: 'Write attrs',
          delete: 'Delete',
          readControl: 'Read control',
          writeDac: 'Write DAC',
          writeOwner: 'Write owner',
          synchronize: 'Synchronize',
          accessSystemSecurity: 'System security',
          maximumAllowed: 'Maximum allowed',
          genericAll: 'Generic all',
          genericExecute: 'Generic execute',
          genericWrite: 'Generic write',
          genericRead: 'Generic read'
        },
        inheritLabel: 'Inheritance',
        inherit: {
          objectInherit: 'Object inherit',
          containerInherit: 'Container inherit',
          noPropagateInherit: 'No propagate',
          inheritOnly: 'Inherit only',
          inherited: 'Inherited',
          auditSuccess: 'Audit success',
          auditFailure: 'Audit failure'
        },
        lossCodes: {
          denyAce: 'Deny entry',
          aceOrder: 'Entry order',
          granularRights: 'Granular rights',
          appendWithoutWrite: 'Append without write',
          richInheritance: 'Rich inheritance',
          inheritOnly: 'Inherit-only',
          noPropagateInherit: 'No-propagate inheritance',
          posixMask: 'POSIX mask',
          creatorTrustee: 'Creator trustee',
          auditing: 'Auditing (SACL)',
          unmappableTrustee: 'Unmappable trustee',
          posixMaskWidening: 'POSIX mask widening',
          defaultAclWidening: 'Default ACL widening'
        },
        addEntry: 'Add entry',
        removeEntry: 'Remove entry',
        writeModel: 'Write as',
        writeModelHint:
          'Native (posix) writes without translation; NT entries on this posix share are translated — a loss that restricts asks for the acknowledgement, a loss that would OPEN access is refused outright.',
        writeModelNtShare:
          "This share's authority is the NT descriptor: writing POSIX entries here would throw the rich ACL away, so only NT writes are offered (posix is refused by the API).",
        notRepresentable: 'Not representable — no checkbox makes it true: correct the entries the message names.',
        save: 'Save ACL'
      }
    },

    // NAS-3 (2026-08-05, PROPOSAL pending ratification): the OPTIONAL
    // platform-managed Kea DHCPv4 service, anchored to a TFTP-enabled
    // MultiSCSI share. Every mutation is sudo-gated — the invasive-service
    // warning is stated wherever a mutation starts.
    dhcp: {
      nav: 'DHCP/PXE',
      title: 'DHCP services',
      createTitle: 'Create DHCP service',
      invasiveWarning:
        "A DHCP server answers EVERY machine on the segment, and two authoritative servers on one network corrupt each other's leases. If this site already runs a DHCP, do NOT create one here — configure options 66/67 (next-server, filename) on the existing server instead; the platform cannot detect foreign DHCP servers, so this warning is all the protection there is.",
      optionalHint:
        "Optional by design: this exists only for networks WITHOUT a DHCP. The daemon runs on the anchor share's authority node, bound to its floating service address, and follows it on failover.",
      name: 'Name',
      nameHint: 'Lowercase letters, digits and dashes — cosmetic, nothing on the wire embeds it',
      fileShare: 'Anchor share',
      fileShareHint:
        'A MultiSCSI share with TFTP among its protocols — the PXE chain (DHCP answer, TFTP fetch, boot) points at its service address; immutable after creation',
      noShares: 'No TFTP-enabled MultiSCSI share exists — create one first (the tftp protocol on a file share).',
      subnet: 'Subnet (CIDR)',
      subnetHint: 'The served IPv4 subnet, e.g. 192.0.2.0/24 — unique cluster-wide: one service per subnet, ever',
      poolFrom: 'Pool from',
      poolTo: 'Pool to',
      poolHint: 'The dynamic lease range, inside the subnet',
      leaseSeconds: 'Lease (seconds)',
      leaseHint: 'Empty = 3600',
      routers: 'Gateways (option 3)',
      routersHint:
        'Comma list of IPv4 addresses, in order — empty: the option is not sent (honest for install-only segments)',
      dnsServers: 'DNS servers (option 6)',
      dnsHint: 'Comma list — empty: not sent',
      domainName: 'Domain (option 15)',
      bootEnabled: 'Send PXE boot options (66/67)',
      bootHint:
        "Every offer carries next-server/filename, split by client architecture (option 93): BIOS clients get filename (default pxelinux.0), UEFI x64 clients get efiFilename (default grubx64.efi). Empty fields mean those defaults; next-server defaults to the anchor share's service address.",
      nextServer: 'Next-server (option 66)',
      filename: 'Boot filename (option 67)',

      // #181 ondata 5: the UEFI half of the Q3 split enters the forms, the
      // reservations become editable rows, and the service earns its REAL
      // PATCH (before: enabled-only — de facto immutable).
      efiFilename: 'UEFI boot filename (option 67)',
      efiFilenameHint:
        'For UEFI x64 clients (option 93 = 0x0007/0x0009); empty = grubx64.efi. Point it at the uploaded shim for Secure Boot clients.',
      reservationsHint:
        'Static MAC→address reservations — MACs lowercase colon form, addresses inside the subnet; MACs and addresses unique within the service.',
      reservationMac: 'MAC',
      reservationAddress: 'Address',
      reservationHostname: 'Hostname (option 12)',
      reservationAdd: 'Add reservation',
      editTitle: 'Edit DHCP service {name}',
      editHint:
        'Every serving-affecting change is a config write converging via the reconcile pass (Kea re-render + daemon reload). A subnet change re-runs the uniqueness check.',
      fileShareImmutable: 'Immutable — re-anchoring is a new service',
      subnetEditHint: 'IPv4 CIDR — a change re-runs the uniqueness check (409 on collision)',
      enabled: 'Enabled',
      parked: 'Parked',
      parkHint:
        "Disabled = parked: config kept, daemon stopped — the honest alternative to delete when the customer's own DHCP returns.",
      phase: 'Phase',
      phases: {
        pending: 'Pending',
        running: 'Running',
        degraded: 'Degraded',
        stopped: 'Stopped',
        error: 'Error'
      },
      leaseCount: 'Leases',
      daemonNode: 'Serving node',
      daemonState: 'Daemon',
      reservations: 'Reservations',
      bootOptions: 'Boot options',
      bootNone: 'None — lease-only (boots nobody)',
      observedAt: 'Observed at',
      notObserved: 'Not yet observed',
      relayWarningTitle: 'Server warning',
      toggle: {
        parkAction: 'Park (stop the daemon)',
        resumeAction: 'Resume',
        parkTitle: 'Park DHCP service {name}?',
        resumeTitle: 'Resume DHCP service {name}?',
        parkText: 'The daemon stops; the configuration stays. Leased machines keep their leases until expiry.',
        resumeText:
          "The daemon starts again on the anchor share's authority node. Make sure no other DHCP serves this subnet.",
        submitPark: 'Park',
        submitResume: 'Resume'
      },
      delete: {
        title: 'Delete DHCP service {name}?',
        warning:
          'Synchronous: the row goes and the reconcile pass stops the Kea daemon. Leased machines keep their leases until expiry — a DHCP disappearing is harmless to them.'
      },
      sudoRequired: 'Every DHCP mutation is sudo-gated — enter your password to elevate.'
    },

    // DISKUI-1 (dettature Luca 10+13, 2026-08-06): the Local disks page —
    // L1-L3 of the DISK-1 chunk stack for one selected node. The shared
    // stack vocabulary (columns, dialogs, phases) stays under
    // cluster.nodes.* — the node-detail tabs and this page speak with ONE
    // voice; only the page-specific strings live here.
    localDisks: {
      title: 'Local disks',
      node: 'Node',
      nodeHint: 'Disks, chunks and raids are node-local — pick the node whose stack to manage',
      noNodes: 'No cluster node in the registry yet',
      tabs: {
        disks: 'Disks',
        raids: 'Raids'
      },
      disks: {
        markSelected: 'Mark usable',
        unmarkSelected: 'Unmark',
        markAndChunk: 'Mark for use'
      },
      chunks: {
        censusDenied: 'Reading the chunk census requires the chunk.read permission.'
      },

      // Micro-round tab unico (dettatura Luca 2026-08-06): the combined
      // mark-and-carve dialog — count-driven, the API size is DERIVED.
      markChunk: {
        markTitle: 'Mark {device} for use and carve it into chunks',
        chunkTitle: 'Carve {device} into chunks',
        note: 'One flow: the disk is marked usable AND carved into equal anonymous chunks. Pick HOW MANY chunks — the per-chunk size the API takes (whole GiB) is derived by floor division, and the census confirms the final total after carving (LVM metadata overhead can shave the tail).',
        countLabel: 'Number of chunks',
        countHint: 'The chunk size is derived: floor(disk GiB / count)',
        tooMany: 'Too many chunks — the derived size falls below 1 GiB',
        direct: 'Enter the chunk size directly',
        derivedRegime: 'Resulting regime: {regime}',
        markFailedPrefix: 'Marking failed — nothing was carved',
        chunkFailedPrefix: 'The disk is now MARKED but carving failed — retry with Create chunks on its row',
        submitBoth: 'Mark & carve'
      }
    },

    // DISKUI-1 (dettatura Luca 13, 2026-08-06): the vDisk page — L4 as its
    // own section beside Local disks. 'vDisk' is invariant across locales
    // (ratified spelling). The expand dialog is the DISK-1.1 grow-only verb.
    vdisks: {
      title: 'vDisk',
      expand: {
        action: 'Expand',
        title: 'Expand vdisk {name}',
        hint: "Grow-only, in whole chunks of the disk's own granule — there is no shrink, ever. JBOD form only this round (raid expand arrives with DISK-1.2). On a cluster-bound vdisk the task follows the capacity up the stack.",
        addChunks: 'Additional chunks',
        result: 'From {current} GiB to {target} GiB',
        submit: 'Expand',
        raidBlock: 'RAID-form expand arrives with DISK-1.2 — only jbod vdisks expand this round',
        notReady: 'Only a ready vdisk expands',
        noCensus: 'The chunk census is unavailable (chunk.read) — the target size cannot be computed here.'
      },

      // GUI-VERBI DISK-2 #50 (2026-08-29): vacate one member disk of the
      // raid onto another marked disk, LIVE — the emptying gesture behind a
      // disk removal (mdadm --replace: the kernel copies from the live
      // members, no degraded window — measured on the DISK-2 bench).
      migrate: {
        action: 'Migrate member disk',
        title: 'Migrate a member disk of {name}',
        submit: 'Start migration',
        hint: "Moves ONE member disk's share of the raid onto another marked disk of the same node, LIVE — the array keeps serving (mdadm --replace copies from the live members, no degraded window). The source is released only after the copy converges.",
        jbodBlock:
          'Migrate a jbod with the cross-node mirror — the raid form is the only live-replace lane (ratifica 2026-08-28)',
        externalBlock: "An external LUN has no local chunks to migrate — its redundancy is the foreign SAN's",
        noRaid: 'The raid census is unavailable (raid.read) — the member list cannot be shown here.',
        noCandidates: 'No destination disk: every marked+chunked disk of this node is already a member of this raid.',
        from: 'Member disk to vacate',
        fromHint: "A current member of the vdisk's raid",
        to: 'Destination disk',
        toHint: 'A marked disk of the same node, not already a member',
        candidate: '{disk} — {free} free chunks × {size} GiB',
        fit: 'Needs {needed} chunk(s) on the destination — {free} free.',
        noFit:
          'Needs {needed} chunk(s) on the destination but only {free} are free — the admission will refuse (the allocator speaks the same numbers).',
        busyHint:
          "The array is copying (replace/rebuild in progress) — a migrating vdisk keeps serving; progress is the device's own truth."
      }
    },

    // R25 JBOF-DC-1 (2026-10-25, docs/jbof-dc-1-disegno-2026-10-25.md §2 and
    // §5): the MINIMAL Enclosure page — the shared dual-port shelves with
    // their heads and per-disk owner, and the two manual verbs (failover with
    // the fence confirmed FIRST, failback with both heads alive). Full GUI R26.
    enclosures: {
      title: 'Enclosures',
      summary:
        'Dual-port NVMe shelves shared between two heads: one owner per disk, manual failover with the fence confirmed first, orderly failback.',
      empty:
        'No enclosure declared yet — an operator declares one with POST /api/v1/enclosures (CLI: create-enclosure).',
      expand: 'Show the disks',
      collapse: 'Hide the disks',
      columns: {
        name: 'Name',
        phase: 'Phase',
        heads: 'Heads',
        reserve: 'Reserve',
        disks: 'Disks',
        wwid: 'WWID',
        serial: 'Serial',
        model: 'Model',
        size: 'Size',
        owner: 'Owner',
        vdisk: 'vDisk',
        osd: 'OSD',
        seenBy: 'Seen by'
      },
      phases: {
        ready: 'Ready',
        degraded: 'Degraded',
        'failing-over': 'Failing over',
        error: 'Error',
        deleting: 'Deleting'
      },
      actions: {
        failover: 'Failover…',
        failback: 'Failback…'
      },
      failover: {
        title: 'Failover — {name}',
        warning:
          'A double activation of the same disk corrupts BlueStore: the fence of the dead head must have taken effect BEFORE the activation on the survivor. Without the confirmation the request is refused.',
        from: 'From (the dead head)',
        to: 'To (the surviving head)',
        method: 'Fence method',
        methods: {
          'power-off': 'Power off (current cut)',
          'nvme-reservation': 'NVMe reservation on the namespaces',
          'pcie-port': 'PCIe switch ports disabled',
          bmc: 'BMC power off (Redfish/IPMI)',
          declared: 'Declared by the operator'
        },
        confirmed: 'I confirm the fence of the head took effect',
        note: 'Note (recorded in the task)',
        submit: 'Start the failover'
      },
      failback: {
        title: 'Failback — {name}',
        hint: 'Both heads alive: {from} releases the disks (OSDs stopped, VG deactivated), then {to} imports them.',
        from: 'From (the current owner)',
        to: 'To (the head that came back)',
        submit: 'Start the failback'
      },
      started: 'Task started: {task}',
      startedNoTask: 'Request accepted — no task reference returned.',
      taskLink: 'Open the task'
    },

    // Honest placeholders (dettatura Luca 2026-08-06): menu entries that land
    // before their areas do — each page states what arrives and with which
    // round, no fake data. Local disks and vDisk graduated with DISKUI-1.
    placeholders: {
      remoteDisks: {
        title: 'Remote disks',
        body: 'Third-party iSCSI/NVMe-TCP disks for backup and replication arrive with the EXT-1 round.'
      }
    }
  }
}

export const it: typeof en = {
  storage: {
    clusters: {
      title: 'Cluster di storage',
      createTitle: 'Crea cluster di storage',
      name: 'Nome',
      nameHint: 'Minuscole, cifre e trattini',

      // REMODEL R1 (#214, 2026-08-26): la superficie datapool è MORTA — le
      // due metà motore sono risorse di prima classe (CephCluster e
      // MultiscsiCluster) fuse in QUESTA lista, ogni riga etichettata con la
      // sua specie. Il verbo `use` e lo stato unbound sono morti col rename:
      // la creazione fa nascere il fabric.
      // REMODEL R5 (#214, 2026-08-27 — ratifica §3): DUE PAGINE DISTINTE —
      // /storage/ceph-clusters e /storage/multiscsi-clusters; la lista fusa è
      // morta con la sua rotta (nessun alias, dottrina alpha).
      createTitleCeph: 'Crea cluster Ceph',
      createTitleMultiscsi: 'Crea cluster MultiSCSI',
      capacityBar: {
        title: 'Promesso vs usato',
        promised: 'Promesso grezzo (taglia × peso, dichiarati compresi)',

        // ENEA-A-R13: il lato NETTO della promessa (Σ taglie dichiarate) accanto al grezzo.
        promisedNet: 'netto',
        used: 'Usato (osservato)',
        capacity: 'Capacità (osservata)',
        admittable: 'Ancora ammissibile',
        notObserved:
          "Capacità non ancora osservata — l'ammissione rifiuta invece di indovinare (riprova quando il cluster riporta)"
      },
      explorer: {
        error: "Interrogazione dell'esploratore fallita",
        retry: 'Riprova',
        refresh: 'Aggiorna',
        tabSalute: 'Salute',
        tabDemoni: 'Demoni',
        tabPool: 'Pool',
        tabCrush: 'CRUSH',
        checksTitle: 'Check di salute (mon)',
        noChecks: 'Nessun check attivo — il mon riporta HEALTH_OK.',
        checkCode: 'Check',
        checkSeverity: 'Gravità',
        checkSummary: 'Sintesi',
        osdsTitle: 'Censimento OSD (esploratore)',
        osdClass: 'Classe',
        osdWeight: 'Peso / reweight',
        osdUtilization: 'Utilizzo',
        osdLatency: 'Latenza (apply/commit)',
        osdOutAction: 'Metti out (svuota prima di un intervento)',
        osdInAction: 'Rimetti in',
        osdOutTitle: 'Metti {osd} out',
        osdInTitle: 'Rimetti {osd} in',
        osdOutWarning:
          'Il cluster ri-replica via da questo OSD — il movimento dati parte ora. Il claim del vdisk resta: out NON è una rimozione dalla membership.',
        osdInWarning: "L'OSD rientra nel placement — il ritorno dell'out (movimento dati).",
        poolsTitle: 'Tutti i pool',
        poolsHint:
          "L'esploratore mostra la verità: ogni pool, compresi quelli di sistema — sono i volumi a nasconderli.",
        poolName: 'Pool',
        poolOwner: 'Proprietario',
        poolOwnerVolume: 'volume',
        poolOwnerSystem: 'sistema',
        poolProfile: 'size/min_size',
        poolPgNum: 'pg_num',
        poolApplication: 'Applicazione',
        poolUsage: 'Uso',
        poolObjectsCount: '{count} oggetto/i',
        objectsAction: 'Esplora oggetti (rados ls)',
        objectsTitle: 'Oggetti di {pool}',
        objectsHint: 'rados ls paginato — uno strumento diagnostico, mai un data path.',
        objectsPrefix: 'Prefisso del nome',
        objectsSearch: 'Cerca',
        objectsEmpty: 'Nessun oggetto corrisponde.',
        objectsMore: 'Carica altri',
        deepScrubAction: 'Deep-scrub su richiesta',
        deepScrubTitle: 'Deep-scrub del pool {pool}',
        deepScrubHint:
          'Richiede un deep scrub su richiesta di questo pool — un comando mon, costo minimo; lo scrub gira poi coi tempi del cluster.',
        crushTitle: 'Albero CRUSH decodificato',
        crushRules: 'Regole',
        osdFlagsAction: 'Flag OSD (manutenzione)',
        osdFlagsTitle: 'Flag OSD di {name}',
        osdFlagsWarning:
          "Questi flag SOSPENDONO l'auto-riparazione del cluster (noout prima di un riavvio nodo evita la tempesta di rebalance; pause è il freno d'emergenza). Toglili a manutenzione finita.",
        osdFlagsFlag: 'Flag',
        osdFlagsValue: 'Azione',
        osdFlagsValueHint: 'Esplicita, nessun default',
        osdFlagsSet: 'Imposta',
        osdFlagsClear: 'Togli'
      },
      editTitle: 'Modifica il cluster {name}',
      renameHint: 'Cosmetico: i riferimenti interni sono per id e i nomi sul disco incorporano lo uuid, non il nome',
      encryption: 'Cifratura a riposo',
      gatewayAddresses: 'Indirizzi flottanti dei gateway',
      gatewayAddressesNone: 'nessuno — i client usano gli indirizzi propri dei nodi',
      gatewayAddressesHint:
        "Indirizzi DEL CLUSTER, distribuiti a giro sui gateway membri (ognuno ha una casa) e presi da un superstite quando il lease del titolare scade; con l'impostazione di cluster gatewayFailover.rebalance = automatic tornano a casa quando il nodo è di nuovo sano. Si sposta la connettività TCP: i mount NFS hard si fermano e riprendono, lo stato NFSv4 lo ristabilisce il client (Ganesha apre prima una grazia di cluster); SMB non segue.",
      gatewayAddressesEditHint:
        "Un IPv4 nudo per riga (max 16), dentro una subnet client dichiarata, mai l'indirizzo di un nodo, di un binding o di una share. Le modifiche convergono alla passata di riconciliazione.",
      gatewayAddressesInvalid: 'Non è un letterale IPv4 nudo',

      // ENEA-B-R15 (2026-10-24): il tenant a cui ogni indirizzo flottante è assegnato.
      gatewayAddressScopes: 'Tenant di ogni indirizzo di gateway',
      gatewayAddressScopeCommon: 'comune — tutti i tenant',
      gatewayAddressScopeChip: 'tenant {scope}',
      gatewayAddressScopesHint:
        "Un indirizzo assegnato a uno scope è annunciato SOLO alle share e agli store di quello scope e dei suoi discendenti (vince l'antenato assegnato più vicino); un tenant con indirizzi assegnati non vede mai quelli comuni. Togliere dalla lista un indirizzo assegnato ne lascia cadere l'assegnazione; cancellare uno scope con indirizzi assegnati è rifiutato (409).",

      // ENEA-B-R14 (2026-10-24): il tetto di banda per indirizzo flottante di gateway.
      gatewayQos: 'QoS degli indirizzi di gateway',
      gatewayQosReadBps: 'Tetto in lettura per indirizzo di gateway (MiB/s)',
      gatewayQosWriteBps: 'Tetto in scrittura per indirizzo di gateway (MiB/s)',
      gatewayQosHint:
        'Ogni indirizzo flottante riceve lo stesso tetto, applicato dal nodo che lo TIENE (lo shaping segue l’indirizzo) come foglia dell’albero QoS di rete: letture modellate, scritture a policer. Vale per tutte le share servite da quell’indirizzo — la risposta per GATEWAY dove una share Ceph non ha un indirizzo suo. Vuoto = nessun limite; pavimento 1 MiB/s.',
      gatewayQosNone: 'nessun tetto',
      gatewayQosShaped: 'QoS: modellato',
      gatewayQosNotShaped: 'QoS: NON modellato',
      gatewayQosNotReported: 'QoS: non riportato',
      gatewayQosNotReportedHint:
        'Dichiarato in R14: nessuna riga di osservazione porta ancora lo stato della foglia per indirizzo (il titolare lo tiene nel suo log di riconciliazione) — «non riportato» non vuol mai dire «non modellato».',
      leaseStates: {
        fresh: 'tenuto',
        stale: 'stantio — subentro dovuto',
        none: 'non ancora preso'
      },
      encryptionModes: {
        none: 'In chiaro',
        luks2: 'LUKS2 — ogni vdisk membro'
      },
      encryptionHint:
        'Scelta alla creazione e immutabile: ogni vdisk che entra in questo cluster viene cifrato LUKS2 con una chiave coniata dalla piattaforma nel keystore (esporta le chiavi di ripristino in cassaforte da Sistema → Credenziali); i volumi ereditano. Una scelta diversa è un NUOVO cluster e lo spostamento dei dati con backup/restore.',
      encryptionCreateHint:
        'Ogni vdisk che entra viene formattato LUKS2 al join con una chiave coniata dalla piattaforma nel keystore — nessuno digita una passphrase; i volumi ereditano. Spenta di default su MultiSCSI.',
      encryptionCreateHintCeph:
        'Ogni OSD (= ogni vdisk membro) è dm-crypt/LUKS2 con una chiave coniata dalla piattaforma nel keystore — nessuno digita una passphrase; i volumi ereditano. Attiva di default su Ceph.',
      encryptionImmutable:
        'IMMUTABILE — scegli ora: un cluster nasce cifrato o in chiaro, per sempre. Non esiste migrazione in place né ri-cifratura; ripensarci dopo significa un NUOVO cluster e lo spostamento dei dati con backup/restore.',
      encryptionOffWarning:
        'Cluster in chiaro: ogni OSD creato al suo interno scrive i dati su disco in chiaro, per sempre — un cluster nasce cifrato o in chiaro e non può cambiare dopo (la via è un NUOVO cluster + backup/restore).',
      kind: 'Specie',
      kinds: {
        multiscsi: 'MultiSCSI',
        ceph: 'Ceph'
      },
      phase: 'Fase',
      phases: {
        initializing: 'In inizializzazione',
        ready: 'Pronto',
        degraded: 'Degradato',
        error: 'Errore',
        destroying: 'In smantellamento'
      },
      wrongPhase: 'Questa azione richiede il cluster ready o degraded',
      destroying: 'Il cluster è in smantellamento',
      capacity: 'Capacità',
      used: 'Usato',
      provisioned: 'Provisionato',
      members: 'Membri',
      observedAt: 'Osservato il',
      notObserved: 'Non ancora osservato',
      tabOverview: 'Panoramica',
      tabVdisks: 'Vdisk',
      vdisksEmpty:
        "Ancora nessuna osservazione dei vdisk — arrivano quando l'inizializzazione del fabric raggiunge i nodi membri",
      vdisk: {
        node: 'Nodo',
        vdisk: 'Vdisk',
        vg: 'Volume group',
        state: 'Stato',
        size: 'Dimensione',
        observedAt: 'Osservato il'
      },
      create: {
        kind: 'Specie',
        kindHint:
          "MultiSCSI (SAN iSCSI) o Ceph (RBD nativo) — la creazione fa nascere l'intero fabric su questa specie: la scelta è definitiva",
        taskHint:
          'La creazione avvia subito il fabric (fase In inizializzazione) e questo dialog segue il task di nascita fino alla fine.',
        cephHint:
          'Ceph trasforma i membri in un intero cluster Ceph: ogni vdisk membro diventa un OSD, un monitor gira su ogni nodo membro e i volumi sono immagini RBD nel pool del cluster.',
        cephTooFewNodes:
          'Ceph richiede almeno 3 vdisk membri su 3 nodi distinti — la selezione copre {nodes} nodo/i: la creazione sarà rifiutata (il Ceph a nodo singolo è una deroga di sviluppo lato backend).',
        membersTitle: 'Vdisk membri',
        membersHint: 'Scegli i vdisk che contribuiscono al cluster — il fabric nasce su di loro alla creazione.',
        noVdisks: 'Nessun nodo ha ancora un vdisk pronto e libero — creane uno dalla pagina Cluster (Crea vdisk).',
        claimed: 'Reclamato'
      },
      ceph: {
        tabTitle: 'Salute Ceph',
        notCeph: 'Questo non è un cluster Ceph',
        notObserved: 'Ancora nessuna osservazione Ceph — lo sweep dal nodo membro con un monitor non è ancora arrivato',
        health: 'Salute del cluster',
        healths: {
          healthy: 'Sano (HEALTH_OK)',
          degraded: 'Degradato (HEALTH_WARN)',
          error: 'Errore (HEALTH_ERR)'
        },
        fsid: 'fsid',
        monsTitle: 'Monitor',
        monInQuorum: 'in quorum',
        monOutOfQuorum: 'fuori quorum',
        monNotObserved: 'non ancora osservato',
        mdsTitle: 'MDS — il censimento della mappa CephFS',
        mdsNone: 'nessun MDS nella mappa (nessun CephFS su questo cluster)',
        osdsTitle: 'OSD — uno per vdisk membro',
        osd: {
          node: 'Nodo',
          vdisk: 'Vdisk',
          id: 'OSD id',
          up: 'Up',
          in: 'In'
        },
        osdUp: 'up',
        osdDown: 'down',
        osdIn: 'in',
        osdOut: 'out',
        capacityTitle: 'Capacità',
        capacityTotal: 'Totale (raw)',
        capacityUsed: 'Usato',
        capacityAvail: 'Disponibile',
        fsTitle: 'Filesystem (tvstor-fs)',
        fsNone:
          "Non ancora nato — l'UNICO filesystem arriva con la prima file share; una volta nato resta fino a Distruggi",
        fsShares: '{count} share',
        observedAt: 'Osservato il'
      },
      scrub: {
        tabTitle: 'Scrub',
        notMultiscsi:
          'Lo scrub è una superficie MultiSCSI: Ceph fa deep-scrub nativamente (la sua salute sta nel tab Ceph)',
        action: 'Esegui una passata di consistenza sugli array md',
        title: 'Scrub del cluster {name}',
        submit: 'Avvia la passata',
        mode: 'Modalità',
        modeHint: 'Esplicita, senza default: check conta e non tocca nulla; repair è un atto da operatore',
        modes: {
          check: 'Check — conta le discrepanze, non tocca nulla',
          repair: "Repair — lascia che md riscriva una copia sopra l'altra"
        },
        checkHint:
          'La passata gira come task, un figlio per nodo membro; avanzamento e censimento delle discrepanze arrivano nel tab Scrub di questo cluster. Una passata già in volo risponde 409.',
        repairWarning:
          'Su un mirror md NON PUÒ sapere quale copia è sana: repair copia il PRIMO membro sugli altri (arbitrary-copy) o ricalcola la parità su blocchi dati forse marci (parity-recompute) — leggi la colonna della semantica di riparazione nel tab Scrub per QUESTI array prima di confermare. Sudo più il nome del cluster digitato.',
        confirmLabel: 'Nome del cluster',
        policyTitle: 'Politica',
        policyTitleFor: 'Politica di scrub di {name}',
        policyManualOnly: 'Solo manuale — nessuna passata pianificata (la politica è null)',
        policyLine: 'Ogni {days} giorno/i, dalle {hour}:00 UTC, finestra di {duration} h — tetto di velocità: {speed}',
        policyEdit: 'Modifica politica',
        policyMode: 'Pianificazione',
        policyManualOption: 'Solo manuale (nessuna pianificazione)',
        policyScheduledOption: 'Finestra pianificata',
        everyDays: 'Ogni N giorni',
        startHour: 'Ora di partenza (UTC)',
        startHourHint: '0-23, UTC',
        durationHours: 'Finestra (ore)',
        durationHint: '1-24 — una passata che sfora la finestra viene FERMATA e dichiarata partial',
        maxSpeed: 'Tetto di velocità (KiB/s per array)',
        maxSpeedHint: 'Vuoto = default del kernel',
        speedKernelDefault: 'default del kernel',
        statusTitle: 'Stato per array',
        statusNone: 'Ancora nessuna osservazione di scrub — avvia una passata, o aspetta la finestra pianificata',
        colArray: 'Array',
        colAction: 'Azione del kernel',
        colMismatches: 'Discrepanze',
        colLastResult: 'Ultimo esito',
        colRepairSemantics: 'Semantica di riparazione',
        mismatches: '{count} DISCREPANZE',
        results: {
          completed: 'completata',
          partial: 'PARZIALE — non è una verifica'
        },
        repairSemanticsLabels: {
          'arbitrary-copy': 'copia arbitraria',
          'parity-recompute': 'ricalcolo parità'
        },
        repairSemanticsHints: {
          'arbitrary-copy': 'raid1/10: md copia il PRIMO membro sugli altri — non può sapere quale copia è sana',
          'parity-recompute':
            'raid5/6: la parità è ricalcolata dai blocchi dati — un blocco dati marcio viene cristallizzato'
        },
        skippedTitle: 'Array deliberatamente NON verificati (con la ragione):'
      },
      addMember: {
        action: 'Aggiungi un membro',
        title: 'Aggiungi un membro a {name}',
        submit: 'Aggiungi membro',
        cephHint:
          'Un nuovo membro {node, vdisk} = un nuovo OSD. Il vdisk deve essere un vdisk READY e non rivendicato del nodo scelto; il cluster ribilancia da solo sul nuovo OSD. La rimozione di un membro non è offerta di proposito.',
        multiscsiHint:
          'Un nuovo membro {node, vdisk}: la famiglia di init ensure-idempotente si ri-esegue sulla membership cresciuta — il nuovo membro converge, il resto è no-op. La rimozione di un membro non è offerta di proposito.',
        node: 'Nodo',
        vdisk: 'Vdisk',
        alreadyMember: 'già membro',
        claimed: 'rivendicato da un altro cluster',
        notReady: 'non pronto'
      },
      deleteCephBlock:
        "Un cluster Ceph si smantella solo con Distruggi (la scala di pericolo) — l'eliminazione semplice risponde sempre 422",
      destroy: {
        action: 'Distruggi il cluster Ceph',
        title: 'Distruggi il cluster {name}',
        warning:
          'Questo smantella l\'INTERO cluster Ceph: ogni monitor e OSD sui nodi membri viene fermato e deregistrato. Con "Wipe dei device" i vdisk membri vengono AZZERATI — ogni byte di ogni immagine RBD sparisce per sempre.',
        wipeLabel: 'Device membri',
        wipeYes: 'Wipe dei device — byte distrutti, vdisk riutilizzabili',
        wipeNo: 'Byte intatti (forensics) — i vdisk restano inutilizzabili finché non azzerati',
        wipeHint: "Decisione esplicita obbligatoria — non c'è default",
        confirmLabel: 'Nome del cluster',
        confirmHint: 'Digita {name} per confermare lo smantellamento',
        submit: 'Distruggi'
      },
      migrateSecure: {
        action: 'Migra a msgr2 secure',
        title: 'Migra {name} a msgr2 secure',
        warning:
          "La migrazione riavvia a rotazione ogni demone di un cluster in servizio per raggiungere l'UNICA forma msgr2 secure (byte-identica a un cluster nato secure). È idempotente: un cluster già secure risolve con un task senza lavoro, e un fallimento a metà riprende rilanciandola. Sudo più il nome del cluster digitato.",
        confirmLabel: 'Nome del cluster',
        submit: 'Migra'
      }
    },
    volumes: {
      title: 'Volumi',
      createTitle: 'Crea volume',
      name: 'Nome',
      nameHint: 'Minuscole, cifre e trattini',
      datapool: 'Cluster',
      mode: 'Modalità',
      modes: {
        mirror: 'Mirror'
      },
      replicas: 'Ridondanza',
      copies: 'Copie (X)',
      copiesHint: 'Gambe totali del mirror, una per vdisk distinto',
      copiesCephHint:
        'Copie fisiche — la size del pool proprio del volume (1 è solo sviluppo, dietro la deroga a nodo singolo)',
      minInSync: 'Minimo in sync (Y)',
      minInSyncHint: 'Sotto Y gambe in sync il volume smette di accettare scritture',
      sizeGiB: 'Dimensione (GiB)',

      // ENEA-A-R13 «erasure coding» (2026-10-24): l'impronta grezza della
      // promessa (taglia × peso — la stessa quantità che l'ammissione somma)
      // e la ridondanza in due forme del dialogo di creazione.
      rawBytes: 'Impronta grezza',
      rawBytesHint:
        'Quanto costa questa promessa sul cluster: taglia × copie, oppure taglia × (k + m) / k con erasure coding',
      redundancyForm: {
        mode: 'Protezione',
        replicated: 'Replicata',
        erasure: 'Erasure coding',
        optIn:
          'La replicata è il default; l’erasure coding è una scelta per volume — EC 4+2: 300 TB netti costano 450 grezzi invece di 600 a 2 copie',
        erasureCephOnly:
          'L’erasure coding è un concetto Ceph (RADOS) — un volume MultiSCSI è un mirror md sui vdisk membri che hai scelto',
        k: 'Chunk di dati (k)',
        kHint:
          '2..16 — ogni chunk cade su un host OSD distinto (dominio di guasto host); il pool dati nasce erasure-coded con gli overwrite abilitati, un pool replicato 3/2 a fianco tiene i metadati',
        m: 'Chunk di parità (m)',
        mHint:
          '1..8 — il volume sopravvive a m host perduti; Ceph pone min_size = k + min(1, m−1) (misurato: 2+1 → 2, 4+2 → 5): con m = 1 un host giù lascia il pool in servizio SENZA parità di scorta, con m ≥ 2 ne resta una. La scelta dell’offerta è 4+2',
        kmHosts: 'k + m ({km}) supera gli host OSD del cluster ({hosts}) — ogni chunk vuole il suo host',
        capacityLine: 'Netti {net} → grezzi {raw} sul cluster ({weight})',
        netAdmittable: 'Margine netto a questa politica: {net}'
      },
      phase: 'Fase',
      phases: {
        declared: 'Dichiarato — nessun byte ancora',
        materializing: 'In materializzazione',
        ready: 'Pronto',
        degraded: 'Degradato',
        blocked: 'Bloccato — scritture in pausa',
        error: 'Errore',
        deleting: 'In eliminazione'
      },
      specie: 'Specie',
      species: {
        lun: 'LUN',
        filesystem: 'Filesystem',
        object: 'Object'
      },

      // REMODEL R5 (#214): il modello dichiarato detto in superficie — il
      // callout da dichiarato, la ragione non-materializzato sui verbi
      // disabilitati (specchio del 422 volume_not_materialized del backend).
      declaredCallout:
        'Non materializzato — il volume si materializza al PRIMO consumo: esportalo con un Target (LUN), o lascia che una FileShare/ObjectStore lo consumi.',
      notMaterializedBlock:
        'Non ancora materializzato — questo verbo esige byte materializzati (il volume si materializza al primo consumo)',
      materializedAt: 'Materializzato il',
      mdState: 'Stato RAID',
      torn: 'Lacero',
      tornChip: 'LACERO',
      tornWhole: '—',
      tornHint:
        'Un restore ha cominciato a scrivere in questo volume e non è mai arrivato in fondo: i suoi byte non sono né il vecchio contenuto né quello ripristinato. Gli export sono rifiutati. Rilancia il restore fino alla fine (riprende dal suo checkpoint), oppure elimina il volume.',
      tornDetail: 'Task di restore {task} sul nodo {node}, dal {since}',
      tornDetailUnknown: 'Il marcatore salvato non è leggibile — il volume è lacero, i dettagli sono sconosciuti',
      authorityNode: 'Nodo authority',
      exportedBy: 'Esportato da',
      notExported: 'Non esportato',
      exportedBlock: 'Il volume è esportato dal target {targets} — prima elimina il target',
      shareBackedDeleteBlock:
        "Questo volume fa da supporto a una condivisione file — elimina la CONDIVISIONE (il suo dialogo porta la decisione esplicita sulla distruzione dei dati); l'eliminazione diretta viene rifiutata (409)",

      // M4b (#189): il badge di replica DR — il marcatore parlava solo
      // attraverso i rifiuti di expand/qos.
      replicaChip: 'REPLICA DR',
      replicaHint:
        "Replica in sola lettura materializzata dal cluster {cluster} (ultimo stamp ricevuto: {stamp}) — il flusso di ricezione è il suo unico scrittore: target, revert, resize e restore-overwrite sono rifiutati. La promozione della copia DR è un round successivo; l'eliminazione è permessa (la sorgente ri-semina completo se il link lo elenca ancora).",
      observedAt: 'Osservato il',
      notObserved: 'Non ancora osservato',
      tabOverview: 'Panoramica',
      tabLegs: 'Gambe',
      legsEmpty: 'Ancora nessuna osservazione delle gambe',
      legs: {
        vdisk: 'Vdisk',
        node: 'Nodo',
        vg: 'Volume group',
        lv: 'Logical volume',
        state: 'Stato',
        reads: 'Letture',
        fence: 'Fence',
        at: 'Osservato il',

        // PERF-1 (#189): il marchio (W) di md, osservato dallo sweep.
        writeMostlyChip: 'write-mostly (W)',
        writeMostlyHint:
          "md tiene questa gamba write-mostly: le letture le serve la gamba LOCALE dell'authority finché è sana — puro suggerimento di instradamento delle letture, le scritture restano sincrone su ogni gamba (write-behind è vietato). Le gambe remote portano il marchio a ogni assemblaggio; quella locale mai."
      },
      authority: {
        tabTitle: 'Authority / Failover',
        holder: 'Nodo authority',
        unassigned: 'Non ancora assegnata',
        lease: 'Lease',
        leaseExpiresAt: 'Orizzonte del lease',
        epoch: 'Epoch',
        phases: {
          held: 'Attivo',
          stale: 'Scaduto',
          failingOver: 'In failover'
        },
        phaseHints: {
          held: 'Il lease è fresco — il detentore ha rinnovato entro il TTL',
          stale:
            'Il rinnovo è scaduto oltre il TTL: il detentore ha smesso di rinnovare — il volume è candidato al takeover',
          failingOver: "Un task di failover sta trasferendo l'authority (bump di epoch in volo)"
        },
        health: 'Salute della prova di failover',
        healthHint:
          'Se un takeover automatico potrebbe provarsi adesso — un grant del witness o un fencing verificato del peer; un timeout da solo non è mai una prova.',
        witness: 'Witness',
        peerFencing: 'Fencing del peer',
        reachable: 'Raggiungibile',
        unreachable: 'Irraggiungibile',
        notConfigured: 'Non configurato',
        legsTitle: 'Gambe',
        leg: 'Gamba {index}',
        fenced: 'Stale-fenced',
        fencedHint:
          'Il degrade intent è stato registrato prima che md riprendesse ad ackare degradato: a questa gamba mancano scritture confermate ai client. Mai promuovibile automaticamente — rientra solo come bersaglio di resync; la marca si toglie a resync completato.',
        fencedAt: 'Fenced il {at}',
        failover: 'Failover',
        failoverTitle: 'Failover del volume {name}',
        failoverHint:
          "Il takeover richiede la PROVA che il detentore attuale è fermo — un grant del witness o un esito verificato della cascata di fencing, mai un timeout da solo. Senza prova l'azione è rifiutata (il pavimento manuale onesto).",
        failingOverBlock: 'Un failover è già in corso',
        targetNode: 'Nodo di destinazione',
        targetNodeAuto: 'Placement automatico',
        targetNodeHint: 'Solo i nodi con una gamba non fenced sono eleggibili',
        failoverSubmit: 'Failover',
        forcePromote: 'Promozione forzata',
        forcePromoteTitle: 'Promozione forzata della gamba {index} di {name}',
        forcePromoteWarning:
          "PERDITA DATI DICHIARATA: questa gamba è stale-fenced — le mancano scritture GIÀ confermate ai client dopo il suo fence. Promuoverla la rende l'unica verità: ogni altra gamba viene fenced contro il nuovo epoch e risincronizzata da lei, e le scritture confermate scartate svaniscono per sempre.",
        forcePromoteConfirmLabel: 'Nome del volume',
        forcePromoteConfirmHint: 'Digita {name} per confermare la perdita dati dichiarata',
        forcePromoteSubmit: 'Promuovi comunque'
      },
      create: {
        datapoolHint: 'Solo i cluster di storage in fase Pronto o Degradato accettano nuovi volumi',
        noDatapools: 'Nessun cluster di storage è in servizio — creane uno e lascia che il suo fabric si inizializzi.',
        declaredHint:
          'Il volume nasce DICHIARATO: una promessa sincrona di capacità, ammessa contro la capacità osservata del cluster (dimensione × copie). Nessun device viene toccato finché il primo consumatore — un target, o il backing di una share/store — non lo materializza.'
      },

      // M5d (2026-08-04): espansione online — solo crescita, dichiarata nel dialog.
      expand: {
        action: 'Espandi',
        title: 'Espandi il volume {name}',
        current: 'Dimensione attuale: {size} GiB',
        newSize: 'Nuova dimensione (GiB)',
        growOnlyHint:
          "QUI solo crescita — la nuova dimensione deve superare quella attuale; la riduzione è l'azione sua, sudo e a conferma digitata (#34). Il volume resta in servizio mentre cresce.",
        rescanHint:
          'TVStor espande la LUN, non i tuoi dati: gli initiator iSCSI devono fare un rescan per vedere la nuova capacità (es. iscsiadm -m node -R), e la partizione/filesystem dentro il consumatore va espansa dal suo proprietario.',
        tooSmall: 'Deve superare gli attuali {size} GiB',
        notReady: "L'espansione richiede un volume Pronto — ogni gamba deve essere estendibile",
        replicaBlock: 'Una replica DR in sola lettura segue la sua sorgente — espandi il volume sorgente',
        shareBackedBlock:
          'Questo volume fa da supporto a una condivisione file — la capacità è un gesto della condivisione, non del volume: falla crescere dal Ridimensiona della condivisione (GROW-MS: gambe, mirror ed ext4 seguono online)',
        submit: 'Espandi'
      },

      // GUI-VERBI #34 (2026-08-29): lo shrink — sudo + conferma digitata,
      // la dottrina R5 per specie detta a priori (contract actionShrink).
      shrink: {
        action: 'Riduci',
        title: 'Riduci il volume {name}',
        submit: 'Riduci',
        hint: 'Il verso mancante del resize — lo armano sudo e il nome del volume digitato. La nuova dimensione deve essere strettamente minore.',
        newSize: 'Nuova dimensione (GiB)',
        current: 'Dimensione attuale: {size} GiB',
        tooBig: 'Deve essere strettamente minore degli attuali {size} GiB',
        confirmLabel: 'Digita il nome del volume per confermare',
        confirmHint: 'Digita {name} esattamente — la conferma viaggia nella richiesta, il client non la riempie mai',
        declaredHint:
          "Volume dichiarato: lo shrink abbassa la PROMESSA in modo sincrono — nessun device viene toccato, e la capacità liberata torna al margine d'ammissione del cluster.",
        cephFsHint:
          "Volume CephFS: online — la promessa scende sotto la GUARDIA D'AGGREGATO: la somma delle dimensioni delle share del volume deve stare nella nuova taglia (il rifiuto fa i conti; ridimensiona prima le share).",
        objectQuotaHint:
          "Volume object: la quota del pool scende col task — mai sotto l'uso osservato più margine (il rifiuto fa i conti).",
        offlineWarning:
          "Ramo OFFLINE (ext4 su MultiSCSI): i demoni della share si FERMANO, il filesystem viene controllato e ridimensionato, poi ri-servito — una finestra di indisponibilità DICHIARATA, dominata dall'fsck. Il dialogo non aspetta: la verità la dice la fase della riga.",
        lunBlock:
          'Lo shrink a blocchi (LUN) è fuori dal perimetro v1 ratificato — il filesystem dentro la LUN è del consumatore, e i byte oltre il nuovo confine verrebbero tagliati (ratifica 2026-08-28)',
        xfsBlock: 'XFS non si riduce, mai — la via è una nuova share e una copia',
        msObjectBlock:
          "Object store su MultiSCSI: lo shrink è un residuo dichiarato (lo store cavalca l'ext4 del volume — un round successivo)",
        replicaBlock: 'Una replica DR in sola lettura segue la sua sorgente — riduci il volume sorgente',
        snapshotWarning:
          'Questo volume ha {count} snapshot: lo shrink è rifiutato finché esistono snapshot (pin la taglia vecchia — ratifica 2026-08-28). Cancellali prima, o il server risponde 409.'
      },

      // GUI-VERBI DISK-2 #50 (2026-08-29): la device class CRUSH dei pool
      // propri del volume — solo ceph, contata sul censimento OSD.
      // ENEA HP-SAN-1 R19 (2026-10-24): il volume esportato a una coppia di server di I/O.
      ioServerPair: {
        label: 'Coppia di server di I/O',
        none: 'non esportato a una coppia',
        select: 'Esporta a una coppia di server di I/O (facoltativo)',
        selectNone: 'Nessuna coppia',
        selectHint:
          'Il volume nasce esportato alla coppia (il suo pool entra nelle caps della coppia): un OST/MDT/MGT di Lustre. La coppia deve essere pronta sullo stesso cluster Ceph.',
        lustreRole: 'Ruolo Lustre',
        lustreRoleNone: 'Nessun ruolo',
        lustreIndex: 'Indice Lustre',
        roleHint:
          'Descrittivo — lo mostrano il render, la CLI e il runbook R20. Un MDT/MGT deve essere replicato ×3 sulla classe dei metadati della coppia (altrimenti il server rifiuta); un OST ha qualunque ridondanza (EC 4+2 è il default dell’offerta).',
        holder: 'Possessore (lock rbd)',
        holderNone: 'nessuno tiene il lock',
        watchers: 'Watcher',
        roles: { ost: 'OST (dati)', mdt: 'MDT (metadati)', mgt: 'MGT (gestione)' }
      },
      deviceClass: {
        label: 'Classe di device',
        any: 'Qualunque (nessun vincolo)',
        free: 'Qualunque nome di classe CRUSH (R19): hdd, ssd, nvme o una classe tua (es. nvme-meta) — vuoto = nessun vincolo',
        hint: "La classe CRUSH su cui devono stare i pool del volume (solo ceph) — l'ammissione conta gli OSD della classe e rifiuta parlando una classe con troppo pochi.",
        option: '{class} — {count} OSD osservati',
        immutableHint:
          'La ridondanza (classe inclusa) si scrive solo finché il volume è DICHIARATO — dopo la materializzazione rimodellare pool vivi è un round successivo dichiarato'
      },

      // M5d (2026-08-04): il pannello QoS per volume — ENTRAMBI I MOTORI da
      // QoS-2 #36 (2026-08-26, docs/qos2-disegno-2026-08-26.md), con
      // l'onestà sull'enforcement di ciascun motore nel proprio hint
      // (contract Volume.qos).
      qos: {
        tabTitle: 'QoS',
        summary: 'QoS',
        unlimited: 'Illimitato',
        iops: 'Limite IOPS',
        iopsHint: 'Vuoto = illimitato su questo asse',
        mbps: 'Limite di banda (MiB/s)',
        mbpsHint: 'Vuoto = illimitato su questo asse',
        hint: 'I limiti si applicano per volume e sopravvivono ai guasti dei nodi (vivono nel cluster Ceph). Enforcement, onestamente: un token bucket rigoroso rispettato dai consumatori RBD nativi (librbd); un volume Ceph esportato iSCSI NON viene modellato — la mappatura krbd del gateway non valuta la QoS librbd.',
        hintMultiscsi:
          "I limiti si applicano per volume al gateway iSCSI (un token bucket vero, totali per asse su IOPS e banda) e seguono il volume nei failover. Onestamente: cambiare i valori di una QoS attiva è un'operazione dal vivo; accendere o spegnere la QoS su un volume esportato ristabilisce brevemente la LUN (gli initiator con normali retry/MPIO la cavalcano).",
        apply: 'Applica limiti',
        clear: 'Rimuovi limiti',
        invalid: 'I limiti sono interi positivi',
        replicaBlock: 'Una replica DR in sola lettura non esporta nulla da modellare',
        applied: 'Limiti applicati — il task di enforcement è in esecuzione'
      }
    },
    snapshots: {
      tabTitle: 'Snapshot',
      createTitle: 'Crea snapshot',
      name: 'Nome',
      nameHint: 'Minuscole, cifre e trattini',
      stamp: 'Stamp',
      phase: 'Fase',
      phases: {
        pending: 'In attesa',
        ready: 'Pronto',
        error: 'Errore',
        deleting: 'In eliminazione'
      },
      observed: 'Osservato',
      states: {
        complete: 'Completo',
        partial: 'Parziale',
        missing: 'Mancante'
      },
      notObserved: 'Non ancora osservato',
      empty: 'Ancora nessuno snapshot',
      notReady: 'Solo gli snapshot Pronti permettono questa azione',
      volumeNotReady:
        'Gli snapshot richiedono un volume Pronto — un set degradato catturerebbe gambe non identiche (422 del contract)',
      clone: 'Clona in un nuovo volume',
      cloneTitle: 'Clona lo snapshot {name}',
      cloneHint:
        'Il clone è un volume indipendente sullo stesso cluster e sugli stessi vdisk — eliminare lo snapshot (o il volume sorgente) non lo invalida mai.',
      cloneName: 'Nome del nuovo volume',
      revert: 'Riporta il volume a questo snapshot',
      revertTitle: 'Riporta allo snapshot {name}',
      revertWarning:
        'Il contenuto attuale del volume {volume} sarà SOSTITUITO da questo snapshot. Gli snapshot successivi restano disponibili, ma le modifiche mai snapshottate vanno perse.',
      revertConfirmLabel: 'Nome del volume',
      revertConfirmHint: 'Digita {name} per confermare il revert',
      revertSubmit: 'Riporta',

      // ENEA-A-R5 (2026-10-23): la retention CONGELATA — un vocabolario per
      // gli snapshot di volume e gli snapshot-risorsa della share.
      retention: {
        column: 'Retention',
        none: 'Nessuna',
        until: 'Fino al {date}',
        retainUntil: 'Conserva fino a',
        retainUntilHint:
          'Facoltativa. Almeno un minuto e al massimo 100 anni da ora. CONGELATA una volta impostata: si può solo allungare, mai accorciare né togliere — nemmeno un amministratore.',
        createHint:
          'La retention è una promessa del prodotto: finché dura lo snapshot non si cancella e i dati sotto di esso non si distruggono (il server risponde 409 con la data). Non recinta la CLI dello storage sottostante.',
        frozenHint:
          'Retention congelata: cancellazione e distruzione rifiutate fino a questo istante; si può solo allungare.',
        deleteBlocked: 'Retention in corso: la cancellazione è rifiutata finché non termina (409 retention_active)',
        extend: 'Allunga la retention',
        extendTitle: 'Allunga la retention di {name}',
        extendSubmit: 'Allunga',
        extendHintActive:
          'La retention termina attualmente il {until}. Si accetta solo un istante successivo — accorciarla o toglierla è rifiutato (422 retention_frozen).',
        extendHintNone:
          'Questo snapshot non ha retention. Impostarla la congela: da quel momento si può solo allungare.'
      },
      diff: {
        action: 'Confronta',
        compareFrom: 'Confronta a partire da questo snapshot',
        title: 'Confronta gli snapshot di {name}',
        hint: 'Elenca QUALI BLOCCHI sono cambiati fra due istanti, così un backup copia il delta invece di rileggere il volume. È una lettura: non modifica nulla.',
        from: 'Da (riferimento)',
        fromHint: "L'estremo più vecchio — il punto che il backup ha già",
        to: 'A',
        toHint: "L'estremo più recente: uno snapshot successivo, o il volume vivo",
        head: 'Volume vivo',
        headHint:
          "Verso il volume vivo l'estremo recente non è un istante: i blocchi possono cambiare mentre il diff gira, quindi la risposta è tutto ciò che POTREBBE essere cambiato dal riferimento.",
        run: 'Confronta',
        running: 'Calcolo dei blocchi cambiati in corso…',
        volumeNotReady: 'Solo i volumi Pronti o Degradati si possono confrontare',
        noBaseline: 'Serve almeno uno snapshot Pronto come riferimento',
        noResult: 'Il task è finito senza un risultato leggibile',
        granularity: 'Granularità',
        granularityHint:
          'La più piccola unità che lo storage sa marcare come cambiata: la risposta non è più fine di così, e un backup arrotonda a questa le sue letture.',
        extentCount: 'Extent cambiati',
        changedBytes: 'Byte cambiati',
        changedBytesPartial:
          "Almeno questi: è caricata solo la parte inline dell'elenco — carica l'elenco completo per il totale esatto.",
        share: 'Quota del volume',
        extentsEmpty: 'Nulla è cambiato fra questi due punti',
        offset: 'Offset',
        length: 'Lunghezza',
        truncated: "Il risultato porta i primi {inline} extent su {total}: l'elenco completo vive nel log del task.",
        loadFull: "Carica l'elenco completo",
        showing: 'Mostrati {shown} su {total}',
        showMore: 'Mostrane altri {count}'
      }
    },
    hosts: {
      title: 'Host',
      createTitle: 'Crea host',
      editTitle: 'Modifica host {name}',
      edit: 'Modifica nome e IQN',
      name: 'Nome',
      nameHint: 'Minuscole, cifre e trattini',
      iqns: 'IQN degli initiator',
      iqnHint: 'iqn.YYYY-MM.dominio.rovesciato[:suffisso], eui.<16 hex> o naa.<16-32 hex>',
      iqnDuplicate: 'Questo IQN è già in elenco su questo host',
      addIqn: 'Aggiungi IQN',
      removeIqn: 'Rimuovi IQN',
      chapFence:
        "Il controllo accessi è solo per IQN degli initiator — un IQN è una dichiarazione di identità, non un segreto. L'autenticazione CHAP arriva col round delle credenziali; tieni i consumatori su una rete dati fidata.",
      removalWarning:
        "Rimuovere un IQN toglie il suo accesso al prossimo passo di reconcile e disconnette quell'initiator — è un'azione sul data path."
    },
    targets: {
      nav: 'LUN',
      title: 'Target',
      createTitle: 'Crea target',
      name: 'Nome',
      nameHint: 'Minuscole, cifre e trattini',
      volume: 'Volume',
      volumeHint: 'Un target esporta un volume alla LUN 0 — in elenco solo i volumi in servizio senza target',
      noVolumes: 'Nessun volume esportabile — deve essere Pronto o Degradato e non già esportato.',
      protocol: 'Protocollo',
      protocols: {
        iscsi: 'iSCSI',
        rbd: 'RBD nativo'
      },
      protocolHint:
        'iSCSI serve entrambe le specie (un volume Ceph passa dal ponte gateway rbd-map); RBD nativo serve solo volumi su un cluster Ceph',
      rbdNoVolumes: 'Nessun volume Ceph esportabile — RBD nativo serve solo volumi su un cluster Ceph.',
      rbdHostsHint:
        "Ogni host riceve la propria identità cephx coniata dalla piattaforma e limitata a pool/immagine; rimuovere un host la revoca — un'azione sul data path.",
      hosts: 'Host',
      hostsHint: "Il set di ACL è l'unione degli IQN degli host selezionati",
      noAclWarning:
        'Nessun host selezionato: il target non ha ACL — nessun initiator può collegarsi (un export parcheggiato).',
      noAcl: 'Nessuna ACL — nessun accesso',
      noHostsDefined: 'Nessun host è ancora definito — creane uno dalla pagina Host per concedere accesso.',
      iqn: 'IQN del target',
      edit: 'Modifica nome e host',
      editTitle: 'Modifica il target {name}',
      editWarning:
        'Rimuovere un host disconnette i suoi initiator dalla LUN — i cambi di host sono azioni sul data path, convergono al prossimo passo di reconcile.',
      deleteTitle: 'Eliminare il target {name}?',
      deleteWarning:
        "Gli initiator collegati perdono la LUN — eliminare il target è un'azione sul data path. L'export viene smantellato su ogni nodo.",
      tabTitle: 'Target',
      tabOverview: 'Panoramica',
      tabExport: 'Export',
      empty: 'Questo volume non è ancora esportato da nessun target',
      manageHint: 'I target si gestiscono dalla pagina Storage → Target.',
      pathsTitle: 'Path (per nodo)',
      pathsEmpty: 'Ancora nessuna osservazione dei path',
      observedAt: 'Osservato il',
      notObserved: 'Non ancora osservato',
      path: {
        node: 'Nodo',
        portal: 'Portale',
        alua: 'ALUA',
        observedAt: 'Osservato il'
      },
      chap: 'CHAP',
      chapNone: 'Nessuno — solo ACL per IQN',
      chapCredential: 'Credenziale CHAP',
      chapCredentialHint:
        "Una credenziale CHAP iSCSI del keystore — il segreto si risolve al momento dell'uso e qui non si mostra mai",
      chapMutual: 'CHAP mutual',
      chapMutualHint:
        "Il target autentica anche sé stesso verso l'initiator — serve una credenziale con la coppia mutual",
      chapWarning:
        'Abilitare, ripuntare o rimuovere il CHAP disconnette gli initiator che non corrispondono — azione sul data path, converge al prossimo passo di reconcile.',
      chapNoCredentials:
        'Nessuna credenziale CHAP iSCSI elencabile — creane una dalla pagina Credenziali (o chiedi credential.read).',
      rbd: {
        title: 'Info di mount RBD',
        monitors: 'Monitor',
        pool: 'Pool',
        image: 'Immagine',
        clientsTitle: 'Identità client',
        client: {
          host: 'Host',
          entity: 'Entità cephx',
          credential: 'Credenziale'
        },
        clientsEmpty: 'Nessun host selezionato — nessuna identità coniata (un export parcheggiato)',
        noSecret:
          "La chiave cephx non si mostra mai qui: vive write-only nel keystore e si risolve al momento dell'uso.",
        notBuilt: 'Non ancora costruito — il task di export non è ancora arrivato',
        copy: 'Copia',
        copied: 'Copiato'
      }
    },
    fileShares: {
      title: 'Filesystem',
      createTitle: 'Crea condivisione file',
      name: 'Nome',
      nameHint:
        'Minuscole, cifre e trattini — è anche il nome esportato (path NFS /nome, nome share SMB, etichetta SFTP)',
      datapool: 'Cluster',
      sizeGiB: 'Dimensione (GiB)',
      sizeImmutableHint:
        "La capacità non cambia mai via PATCH — il ridimensionamento è l'azione propria della condivisione (#34: cephfs in entrambi i versi; una share MultiSCSI si ridimensiona coi verbi del suo volume)",
      used: 'Usato',

      // GUI-VERBI #34 (2026-08-29): l'azione resize — cephfs entrambi i
      // versi (quota sincrona); MultiSCSI rimanda ai verbi del volume.
      resize: {
        rowLabel: 'Dimensione / resize',
        action: 'Ridimensiona',
        title: 'Ridimensiona la condivisione {name}',
        submit: 'Ridimensiona',
        hint: "Entrambi i versi: crescita dentro la promessa del volume, riduzione sopra l'uso osservato più margine — i rifiuti fanno i conti.",
        growHint:
          "CRESCITA: l'aggregato delle share del volume deve continuare a stare nella promessa del volume (il rifiuto fa i conti — espandi prima il volume). La quota converge entro una passata di reconcile.",
        shrinkHint:
          "RIDUZIONE: mai sotto l'uso OSSERVATO della share più margine (ceph accetta una quota sotto i byte usati e la share va oltre il pieno: le scritture si bloccano — da qui il pavimento; una share non osservata rifiuta onestamente).",
        newSize: 'Nuova dimensione (GiB)',
        current: 'Dimensione attuale: {size} GiB',
        sameSize: 'Deve differire dagli attuali {size} GiB',
        msGrowHint:
          "CRESCITA (MultiSCSI): la share È il suo volume — qui crescono le gambe e il mirror del volume di supporto (ri-ammessi contro la capacità osservata del cluster) e poi l'ext4 ONLINE, mentre la share continua a servire. Gli snapshot sono ammessi. Una taglia minore è lo shrink del volume, non questo dialogo.",
        msShrinkViaVolume:
          "Lo shrink di una share MultiSCSI passa dall'azione sudo a conferma digitata del volume (la finestra offline dell'ext4)",
        engineUnknown:
          'Il motore della condivisione non è leggibile adesso (catalogo cluster degradato) — riprova quando lo è',
        volumeLink: 'apri il volume di supporto'
      },
      protocols: 'Protocolli',
      protocolsHint: 'Scegli almeno una scheda — ogni protocollo apre il suo pannello di configurazione qui sotto',
      protocolNames: {
        nfs: 'NFS',
        smb: 'SMB',
        sftp: 'SFTP',
        ftp: 'FTP',
        tftp: 'TFTP',
        svcgssd: 'GSS (Kerberos NFS)'
      },

      // SHARE-1PROTO (#213, 2026-08-24): il create è un ELENCO SCHEDE diviso
      // in due tab — il tab fissa il modello dei permessi (immutabile), ogni
      // tab mostra SOLO i protocolli legali per quel modello.
      modelTabs: {
        posix: 'ACL POSIX',
        nt: 'ACL ricche NT'
      },
      protocolCards: {
        nfs: 'Il filesystem di rete Unix/Linux — allow-list di host (AUTH_SYS) o Kerberos su un realm AD.',
        smb: "La condivisione Windows — le ACL ricche NT sono l'autorità: i permessi gestiti da Esplora risorse si ritrovano identici.",
        sftp: 'Trasferimento file su SSH — chroot nella condivisione, utenti file con password o chiavi.',
        ftp: 'Trasferimento FTPS (TLS esplicito) con intervallo di porte passive dichiarato.',
        tftp: "L'albero PXE/netboot — non autenticato, sola lettura per default, solo MultiSCSI."
      },
      ntExplorerHole:
        "Il web explorer su una condivisione NT applica il perimetro kiosk (liste utenti + writable), NON le ACL NT file per file — l'enforcement DACL sul piano web è un round dedicato, dichiarato.",
      serviceAddress: 'Indirizzo di servizio',
      serviceAddressHint:
        "L'indirizzo IPv4/IPv6 a cui si collegano i client — la piattaforma lo assegna all'interfaccia dati del nodo authority e lo sposta al failover (gratuitous ARP)",
      serviceAddressCeph:
        'Nessun indirizzo flottante su una condivisione Ceph in questo round: i client si collegano agli indirizzi dati dei nodi membri, active/active.',
      serviceAddressOn: 'Indirizzo tenuto da',
      phase: 'Fase',
      phases: {
        pending: 'In attesa',
        creating: 'In creazione',
        ready: 'Pronta',
        degraded: 'Degradata',
        error: 'Errore',
        deleting: 'In eliminazione'
      },
      tabOverview: 'Panoramica',
      tabExports: 'Export',
      tabClients: 'Client',
      tabQuota: 'Quota',
      mountedOn: 'Nodo di mount',
      cephActiveActive:
        'Ogni nodo membro (attivo/attivo per NFS, SFTP e FTP; SMB lo serve un solo gateway proprietario)',
      path: 'Path',
      volume: 'Volume di supporto',
      capacity: 'Capacità',
      free: 'liberi',
      observedAt: 'Osservato il',
      notObserved: 'Non ancora osservato',

      // #189 GUI-RESIDUI: il filesystem con cui la condivisione è NATA —
      // registrato all'ammissione, mai riscritto. `cephfs` è il render del
      // null (CephFS È il filesystem), non un valore dell'enum di contract.
      fsType: 'Filesystem',
      fsTypes: {
        xfs: 'XFS',
        ext4: 'ext4',
        cephfs: 'CephFS'
      },

      // #111 (#189): il blocco quota per-cartella + la sua osservazione.
      quota: {
        createLabel: 'Quota radice (MiB)',
        createHint:
          'Facoltativa: un limite sulla radice della condivisione, più stretto della taglia. Si stringe, si sposta o si toglie dopo dal tab Quota.',
        multiscsiHint:
          "Project quota sul filesystem montato: il kernel conta nell'istante della scrittura, quindi i limiti fermano ESATTAMENTE, ovunque nell'albero, e sono ereditati dalle sottocartelle create dopo.",
        cephLateHint:
          "Su una condivisione Ceph il limite vive sulla RADICE DEL SUBVOLUME ed è un allarme che arriva tardi, non un muro: le scritture possono continuare ad atterrare per qualche secondo oltre il limite (l'MDS propaga la statistica d'uso con ritardo — misurati 7-8 s a riposo). Nessuna tolleranza è promessa di proposito; dimensiona il limite di conseguenza.",
        scope: 'Quota',
        scopeNone: 'Nessuna quota',
        scopes: {
          share: 'Solo radice della condivisione',
          subtree: 'Limiti per cartella (subtree)'
        },
        cephSubtreeBlock:
          'rifiutato su una condivisione Ceph: dentro una sottocartella il limite non sarebbe in ritardo, sarebbe ASSENTE (422 quota_scope_not_supported)',
        rootLimit: 'Limite radice (MiB)',
        rootLimitRequired:
          'Obbligatorio — uno scope senza numero non dichiara nulla. Min 1 MiB, mai sopra la taglia della condivisione (una quota stringe, non espande mai).',
        rootLimitOptional:
          'Facoltativo coi limiti per cartella: vuoto = nessun tetto sulla radice, solo sulle cartelle elencate.',
        dirsTitle: 'Limiti per cartella',
        dirsHint:
          'Ogni voce è un realm di project quota: la cartella viene creata se manca, e il limite è ereditato da tutto ciò che nasce sotto. I path sono relativi alla radice della condivisione; i realm non possono annidarsi.',
        dirPath: 'Cartella',
        dirLimit: 'Limite (MiB)',
        dirAdd: 'Aggiungi cartella',
        dirDuplicate: 'La stessa cartella è elencata due volte',
        dirNested:
          'Un realm si annida dentro un altro — due limiti sovrapposti su un albero renderebbero senza risposta «quale limite ha morso»',
        saved: 'Quota salvata — una quota è pura config e converge al passo di reconcile (~30 s).',
        usageTitle: 'Uso (come osservato)',
        usageRoot: 'radice della condivisione',
        usageUsed: 'Usato',
        usageLimit: 'Limite in vigore',
        usageObservedAt: 'Osservato il',
        usageNotApplied: 'non ancora applicato',
        usageNoQuota:
          'Nessun blocco quota — niente da osservare (assente significa NON OSSERVATO, mai uno zero inventato).',
        usageNotObserved: "Il nodo che serve non ha ancora riportato l'uso della quota."
      },
      identity: {
        label: 'Sorgente di identità',
        localHint:
          'Identità locali (il comportamento NAS-1): NFS si fida della allow-list (AUTH_SYS), SMB/SFTP/FTP autenticano utenti file locali. IMMUTABILE dopo la creazione — passare a una directory dopo significa una condivisione nuova più una copia.',
        directoryHint:
          'La condivisione prende le identità da questo realm — SMB ammette i suoi principal di dominio, e su un realm AD NFS può esigere Kerberos. IMMUTABILE dopo la creazione (condivisione nuova + copia è la via del ritorno).',
        modes: {
          local: 'Locale',
          directory: 'Directory'
        }
      },
      aclModel: 'Modello dei permessi',
      aclModels: {
        posix: 'ACL POSIX',
        nt: 'ACL NT (ricche)'
      },
      aclModelHints: {
        posix:
          "Le ACL POSIX sono l'autorità. Questo modello porta NFS, SFTP, FTP e TFTP — mai SMB (SHARE-1PROTO #213: un modello di permessi, un gruppo di protocolli). Immutabile alla nascita.",
        nt: "Il descrittore NT è l'autorità — un amministratore Windows gestisce i permessi da Esplora risorse e li ritrova identici. Questo modello porta SOLO SMB: i protocolli che non sanno leggere le ACL NT non ci viaggiano. Immutabile alla nascita."
      },
      smbLdapBlock:
        'SMB non può viaggiare su un realm LDAP: niente join, niente machine account, nessun hash NT con cui autenticare (ratifica B1). Usa un realm Active Directory, o gli altri protocolli.',
      smbOwnerChip: 'Gateway SMB: {node}',
      smbLeaseChip: 'LEASE SMB NON RINNOVATO',
      smbLeaseUnrenewed:
        "Servita SENZA arbitraggio dal {since}: il gateway continua a servire questa condivisione SMB con un lease che non ha potuto rinnovare perché lo store del cluster non rispondeva. Servire è deliberato (fermarsi trasformerebbe uno store irraggiungibile in un blackout SMB su ogni gateway insieme) — ma da quell'istante un altro gateway può prendere il lease e servire la stessa condivisione, e i lock byte-range non sono più arbitrati. La metrica allarmabile è share.smbLeaseUnrenewedSeconds.",
      principals: {
        title: 'Principal di dominio del realm {realm}',
        entry: 'Principal',
        hint: 'DOM\\utente per una persona, @DOM\\gruppo per un gruppo — DOM è il nome NetBIOS del realm. Vanno diritti in valid users di smb.conf; che un principal ESISTA lo dice la preflight del realm al domain controller.',
        duplicate: 'Questo principal è già in lista',
        add: 'Aggiungi principal',
        remove: 'Rimuovi principal'
      },
      create: {
        datapoolHint: 'Solo i cluster di storage in fase Pronto o Degradato accettano nuove condivisioni',
        noDatapools: 'Nessun cluster di storage è in servizio — creane uno e lascia che il suo fabric si inizializzi.',
        volumeHint:
          "Il volume dichiarato che questa condivisione consuma — su Ceph il CephFS del volume ospita più condivisioni dentro la sua promessa; su MultiSCSI una condivisione è l'intero filesystem del volume",
        noVolumes:
          'Nessun volume consumabile — dichiarane uno dalla pagina Volumi (un volume dichiarato è libero; la creazione della condivisione lo materializza come filesystem).',
        sizeLockedHint: 'Su MultiSCSI la dimensione della condivisione È la dimensione dichiarata del volume',
        cephHint:
          'Su un cluster Ceph la condivisione è un subvolume CephFS esportato da ogni nodo membro — attivo/attivo per NFS, SFTP e FTP, mentre SMB lo serve un solo gateway proprietario (i suoi lock non attraversano i gateway, quelli di NFS sì). La durabilità è il profilo del pool, quindi repliche e indirizzo di servizio non si applicano.',
        multiscsiHint:
          "Su un cluster MultiSCSI la condivisione possiede un volume mirror, formattato e montato dal suo nodo authority — un solo montatore per costruzione; mount, demoni e indirizzo di servizio seguono l'authority al failover."
      },
      nfs: {
        title: 'NFS',
        allow: 'Allow-list',
        allowHint:
          "Indirizzi IPv4/IPv6, subnet CIDR (10.1.2.0/24) o * — tutto il modello di accesso NFS qui: l'identità dichiarata dal client è fidata dentro questa lista",
        allowEntry: 'Host o subnet',
        addEntry: 'Aggiungi voce',
        removeEntry: 'Rimuovi voce',
        duplicate: 'Questa voce è già in elenco',
        squash: 'Root squash',
        squashRoot: 'Squash di root (default sicuro)',
        squashNone: 'Nessun root squash (mount appliance fidati)',
        readOnly: 'Export in sola lettura',
        sec: 'Sapore di sicurezza',
        secHint:
          "krb5 autentica ogni utente con un ticket Kerberos contro il realm AD della condivisione; krb5i aggiunge l'integrità, krb5p la cifratura sul filo. Un client che chiede un sapore diverso è rifiutato — mai un downgrade silenzioso.",
        secGate:
          'AUTH_SYS si fida dello uid che il client dichiara, dentro la allow-list — la postura onesta senza dominio. I sapori krb5* richiedono la condivisione legata a un realm Active Directory (niente realm = niente machine account = niente keytab nfs/).',
        secOptions: {
          sys: 'AUTH_SYS (fiducia al client)',
          krb5: 'Kerberos (krb5)',
          krb5i: 'Kerberos + integrità (krb5i)',
          krb5p: 'Kerberos + riservatezza (krb5p)'
        }
      },
      smb: {
        title: 'SMB',
        users: 'Utenti locali',
        usersHint:
          "Credenziali utente-file del keystore — l'utente referenziato deve avere una password; non esiste mai accesso guest",
        noUsers:
          'Nessuna credenziale utente-file elencabile — creane una dalla pagina Credenziali (o chiedi credential.read).',
        readOnly: 'Export in sola lettura',
        encryption: 'Cifratura SMB3',
        encryptionHint:
          'server smb encrypt di questa share: desired cifra con ogni client che sa farlo (il default del prodotto), required rifiuta le sessioni in chiaro (e rende obbligatoria la firma sul gateway), off non cifra mai',
        encryptionChip: 'cifratura: {mode}',
        encryptionOptions: {
          desired: 'desiderata',
          required: 'obbligatoria',
          off: 'spenta'
        }
      },
      sftp: {
        title: 'SFTP',
        users: 'Utenti',
        usersHint:
          "Le stesse credenziali utente-file — password e/o chiavi pubbliche SSH; un'istanza solo-SFTP in chroot su porta propria, mai l'sshd di gestione",
        readOnly: 'Export in sola lettura'
      },
      exports: {
        readOnly: 'sola lettura',
        readWrite: 'lettura-scrittura',
        squashNone: 'no_root_squash',
        exportName: 'Nome export',

        // #185 (FS-PATCH, 2026-08-19): gli editor post-creazione.
        editTitle: 'Modifiche post-creazione',
        editHint:
          'I principali di dominio, il sapore di sicurezza NFS e le Versioni precedenti si cambiano dopo la creazione con le STESSE regole della nascita — pura config, converge al passo di reconcile (~30 s). La sorgente di identità e il modello dei permessi restano immutabili: cambiarli significa una share nuova più una copia.',
        save: 'Salva modifiche export',
        saved: 'Salvato — le modifiche agli export sono pura config e convergono al passo di reconcile (~30 s).'
      },
      nfsEngine: {
        ganesha: 'Ganesha',
        knfsd: 'NFS del kernel (knfsd)'
      },
      clients: {
        total: 'Client connessi',

        // ENEA-B-R15 (2026-10-24): gli indirizzi flottanti di gateway su cui la share è annunciata.
        tenantAddresses: 'Indirizzi di gateway del tenant',
        tenantAddressesHint:
          'Gli indirizzi flottanti di gateway su cui questa share è annunciata, col titolare attuale: quelli del tenant quando il suo scope ha indirizzi assegnati, quelli comuni del cluster altrimenti. La separazione è per indirizzo e per liste di client/autenticazione, non per bind né firewall.',
        daemonsTitle: 'Demoni',
        empty: 'Ancora nessuna osservazione dei demoni',
        node: 'Nodo',
        protocol: 'Protocollo',
        state: 'Stato',
        pid: 'PID',
        connections: 'Client',
        observedAt: 'Osservato il'
      },
      remount: {
        action: 'Rimonta',
        title: 'Rimonta la condivisione {name}',
        hint: 'Rirende ogni configurazione, rimonta il filesystem e riavvia i demoni della condivisione — il verbo di riparazione quando un demone si è incastrato o un export non è mai arrivato a convergenza. I mount NFS hard e la riconnessione SMB assorbono il breve stallo.',
        submit: 'Rimonta',
        busyBlock: 'La condivisione è in creazione o in eliminazione',
        lockdownBlock:
          "La condivisione è in lockdown — il contract rifiuta il remount su una condivisione bloccata; prima solleva il lockdown (l'azione di sblocco qui, sudo)"
      },
      lockdownChip: 'LOCKDOWN',
      lockdownHint:
        "In lockdown dal {at} per mano di {by} — il servizio è fermo su ogni protocollo, le sessioni del realm files revocate, i token editor distrutti. Explorer ed editor rispondono 409 fino allo sblocco (l'azione sulla riga, sudo).",

      // ENEA-A-R9 «replica file» (2026-10-23): la share REPLICA del mirror.
      replica: {
        chip: 'Replica del mirror',
        chipHint:
          'Replica in sola lettura dal cluster {source} — ultimo snapshot sincronizzato: {snapshot}. Promuovila per renderla una share ordinaria.',
        noneObserved: 'nessuno osservato',
        block:
          'Una replica del mirror è in sola lettura finché non è promossa — la scrive il demone del mirror della sorgente.',
        promote: {
          action: 'Promuovi la replica',
          title: 'Promuovere la replica {name}?',
          point:
            'Replica da {source}: è ferma allo snapshot {snapshot} (scattato {at}, ~{lag} s indietro rispetto a ora). Tutto ciò che la sorgente ha scritto DOPO quel punto è PERSO con questa promozione — dichiarato, non implicito.',
          scale:
            'IL TAGLIO: l’utente cephx del peer su questo cluster viene revocato, così un sync tardivo non può sovrascrivere la share promossa — e il mirror di OGNI share che viaggia sullo stesso peer si ferma. Ciò che è arrivato qui sono i dati degli snapshot, non le risorse snapshot della sorgente né la loro retention. Nessun failback automatico: la protezione inversa è un link nuovo da questo cluster.',
          acknowledge: 'Riconosci il punto di ripristino',
          acknowledgeHint: 'Digita il nome dello snapshot esattamente: {snapshot}',
          acknowledgeNoneHint:
            'Su questa replica non è mai stato osservato uno snapshot sincronizzato — digita none per riconoscere che la promozione può non contenere nulla.',
          confirm: 'Digita il nome della share ({name}) per confermare',
          name: 'Nome promosso (facoltativo)',
          nameHint: 'Sostituisce il nome di piattaforma repl-…; rifiutato se già preso nello scope.',
          submit: 'Promuovi ora',
          done: 'Promozione ammessa (task {task}): l’utente del peer è revocato, la quota riapplicata, la share diventa ordinaria — dalle protocolli ed export.'
        }
      },
      lockdown: {
        action: 'Lockdown (kill-switch)',
        title: 'Mettere in lockdown la condivisione {name}?',
        scale:
          "IL KILL-SWITCH: la condivisione smette di servire su OGNI protocollo entro un battito di riconciliazione (~30 s) — i client connessi la perdono. I suoi token dell'editor documenti vengono distrutti ADESSO: il lavoro non salvato in un editor aperto è perso. Fermare una condivisione che serve è un'interruzione voluta; la pressione finisce nel registro di audit.",
        sessionsNote:
          'La scala intera: ogni sessione del realm files il cui perimetro contiene questa condivisione viene revocata — la sessione INTERA di ciascun fileUser che la condivisione nomina, su ogni condivisione che raggiungeva, fino a un nuovo login. Premere due volte è sicuro: la seconda pressione mantiene lo stesso istante di lockdown e ripassa la scopa.',
        submit: 'Metti in lockdown ora',
        busyBlock:
          'La condivisione non ha finito di nascere, o è in eliminazione — quelle finestre appartengono ai loro task',
        done: 'In lockdown dal {at} per mano di {by} — {sessions} sessioni revocate, {tokens} token editor distrutti.',
        doneBeat:
          "Il servizio si ferma su ogni protocollo entro un battito di riconciliazione (~30 s); una riconciliazione concorrente non può mai riaprirla. L'azione di sblocco solleva il lockdown — le sessioni e i token revocati restano revocati in ogni caso."
      },
      unlock: {
        action: 'Solleva il lockdown',
        title: 'Sollevare il lockdown su {name}?',
        hint: "La condivisione torna a servire al prossimo battito di riconciliazione (~30 s). NIENT'ALTRO viene ripristinato, di proposito: le sessioni e i token editor che il lockdown ha revocato RESTANO revocati — gli utenti rifanno il login, gli editor riconiano. Riaprire una condivisione che un incidente ha chiuso è una decisione con un nome sopra (sudo, a registro).",
        submit: 'Solleva il lockdown'
      },
      delete: {
        title: 'Eliminare la condivisione file {name}?',
        warning:
          "I demoni si fermano e i client connessi perdono la condivisione; l'indirizzo di servizio viene rilasciato e il filesystem smontato.",
        destroyLabel: 'Distruggi anche i dati di supporto',
        multiscsiNote:
          "Su MultiSCSI i byte della condivisione SONO l'unico filesystem del volume consumato: questa cancellazione toglie il servizio e li conserva (il volume sopravvive, ri-consumabile). Distruggere i byte è la DELETE del VOLUME, nella pagina Volumi.",
        keepHint:
          'Senza spunta i byte sopravvivono: su MultiSCSI il volume di supporto continua a vivere come volume ordinario (filesystem intatto); su Ceph il subvolume resta nel filesystem del cluster, non gestito.',
        destroyWarning:
          "Ogni byte della condivisione viene distrutto con lei — incluso il volume di supporto (o il subvolume CephFS) e i suoi snapshot. Non c'è modo di tornare indietro."
      },

      // NAS-3 (2026-08-05, PROPOSAL in attesa di ratifica): FTP(S), TFTP e il
      // blocco di configurazione PXE sulla stessa condivisione.
      ftp: {
        title: 'FTP (FTPS)',
        usersHint:
          "Le stesse credenziali file-user — FTP autentica solo per password, quindi l'utente referenziato DEVE averne una; le sessioni sono in chroot dentro la condivisione",
        passiveFrom: 'Porte passive da',
        passiveTo: 'Porte passive a',
        passiveHint:
          "Il range dati passivo FTPS dichiarato (1024-65535): apri esattamente queste porte sul firewall verso l'indirizzo di servizio. Riservato a livello di cluster — una sovrapposizione con un'altra condivisione viene rifiutata.",
        plainFtp: 'Consenti FTP in chiaro (senza TLS)',
        plainFtpWarning:
          "L'FTP in chiaro trasmette credenziali e dati in cleartext — un opt-out sconsigliato per client legacy. Il default è FTPS esplicito (AUTH TLS sulla porta 21).",
        readOnly: 'Export in sola lettura'
      },
      tftp: {
        title: 'TFTP',
        hint: "TFTP è non autenticato per natura: nessun utente, mai — il controllo d'accesso è la rete, e l'export è in SOLA LETTURA salvo opt-out esplicito. Il mattone del PXE.",
        subpath: 'Directory servita',
        subpathHint:
          'Relativa alla radice della condivisione (niente / iniziale, niente ..) — vuota serve la radice stessa',
        writable: 'Accetta upload (territorio firmware-dump)',
        cephBlock:
          "TFTP è solo MultiSCSI in questo round: il next-server PXE esige l'unico indirizzo di servizio flottante che le condivisioni Ceph non hanno."
      },
      tabBoot: 'Boot / PXE',
      boot: {
        none: 'Nessun TFTP su questa condivisione — aggiungi il protocollo tftp per costruire un albero di boot (il PXE ci viaggia sopra).',
        noPxe:
          'TFTP è servito ma nessun menu PXE è configurato — imposta il blocco pxe via API o CLI (patch-share --pxe-entries).',
        tftpRoot: 'Radice TFTP',
        shareRoot: 'la radice della condivisione',
        access: 'Accesso',
        readOnly: 'sola lettura (default — protocollo non autenticato)',
        writable: 'upload accettati',
        defaultEntry: 'Voce di default',
        timeout: 'Timeout del menu',
        timeoutForever: 'Attende per sempre',
        seconds: 'secondi',
        entries: 'Menu di boot',
        entryName: 'Nome',
        entryLabel: 'Etichetta',
        entryKernel: 'Kernel',
        entryInitrd: 'Initrd',
        entryAppend: 'Append',
        artifactsHint:
          'La piattaforma genera DUE file di menu dalle stesse voci: pxelinux.cfg/default (BIOS, sintassi syslinux) e grub.cfg (UEFI, letto da grubx64.efi — con Secure Boot carica la coppia firmata shim+grubx64 della distro). Loader, kernel, initrd e immagini sono file che TU carichi sulla condivisione (SMB/SFTP/FTP) — una voce di menu che punta a un file mancante fallisce sul client in boot.',

        // #189 GUI-RESIDUI: l'editor — la cucitura read-only chiusa.
        configure: 'Configura menu PXE',
        edit: 'Modifica menu',
        defaultEntryHint: 'Avviata allo scadere del timeout — vuoto sceglie la prima voce',
        defaultFirst: 'Prima voce',
        timeoutHint: "0 = attende per sempre una scelta dell'operatore; vuoto = il default (10 s)",
        pathHint: 'Relativo alla radice TFTP — un artefatto caricato da TE; la piattaforma non lo verifica mai',
        addEntry: 'Aggiungi voce',
        duplicateNames: 'I nomi delle voci devono essere unici nel menu',
        removeMenu: 'Rimuovi il menu PXE (TFTP continua a servire — TFTP senza PXE è solo TFTP)'
      },
      previousVersions: {
        title: 'Versioni precedenti',
        hint: 'La scheda «Versioni precedenti» di Windows, servita dagli snapshot del subvolume CephFS (vfs_shadow_copy2). Solo ceph — lì ACCESA di default (il default segue il costo reale del motore, ratifica B5). Spegnerla conserva le versioni già prese: niente distrugge dati in silenzio.',
        choice: 'Snapshot',
        choiceDefault: 'Default del motore (accesa — oraria, tieni 24)',
        choiceCustom: 'Cadenza personalizzata',
        choiceOff: 'Spenta',
        interval: 'Intervallo (minuti)',
        intervalHint:
          'Da 5 a 10080 — la cadenza vive nei nomi degli snapshot, quindi sopravvive ai riavvii senza doppi scatti',
        keep: 'Tieni',
        keepHint:
          'Da 1 a 64 versioni — la potatura rimuove le più vecchie oltre questo numero; gli snapshot fatti a mano non sono mai contati né potati',
        on: 'Accesa — ogni {interval} min, tieni {keep}',
        off: 'Spenta — le versioni già prese restano sul subvolume',
        engineDefault: 'Default del motore (accesa — oraria, tieni 24)',
        notSupported:
          'Non disponibile su un cluster MultiSCSI (ogni snapshot esposto lì sarebbe un array assemblato e un mount a sé — cucitura dichiarata)'
      },
      tabAcl: 'ACL',

      // ENEA-A-R5 (2026-10-23): gli snapshot-risorsa della share.
      tabSnapshots: 'Snapshot',

      // ENEA-A-R12 (2026-10-23): l'audit client della share.
      // ENEA-B-R14 (2026-10-24): la QoS della share (banda per indirizzo di servizio; gli IOPS sono del volume).
      tabQos: 'QoS',
      qos: {
        intro:
          'Tetti di banda della share, opt-in (vuoto = nessun limite, pavimento 1 MiB/s): attuati come shaping sull’INDIRIZZO DI SERVIZIO della share sul gateway (una foglia dell’albero QoS di rete sotto la classe NAS) — letture modellate, scritture a policer — così ogni protocollo della share ci passa e il tetto segue l’indirizzo al failover. Gli IOPS non sono una manopola della share: li limita il volume a blocchi consumato (set-volume-qos) e la share li eredita.',
        offerPhrase:
          'Ciò che la console e il collaudo dicono: QoS di throughput per indirizzo di servizio (tenant/gateway) e per share su datapool multiscsi; QoS di IOPS e throughput per volume a blocchi; QoS di operazioni e byte per account e per bucket S3 su RGW; su CephFS nessun limite di IOPS per share.',
        cephHint:
          'Una share Ceph non ha un indirizzo di servizio suo — gli indirizzi flottanti dei gateway sono del cluster e ogni gateway serve tutte le share da ogni indirizzo: nessun tetto per share si distingue sul filo (il server risponde 422 qos_not_supported). Il tetto si mette per indirizzo di gateway nella scheda del cluster Ceph (gatewayQos); gli IOPS per share non sono applicabili su CephFS.',
        ceilings: 'Tetti',
        readBps: 'Tetto in lettura (MiB/s)',
        writeBps: 'Tetto in scrittura (MiB/s)',
        fieldsHint:
          'MiB/s (50, 1.5G, 2048K…); vuoto toglie il tetto su quell’asse. Le letture sono modellate (HTB), le scritture a policer (un tetto a scarto, non una coda). Precisione misurata, non promessa: −5 % a 2 e 8 MiB/s su loopback.',
        save: 'Salva i tetti',
        saved: 'Tetti salvati — il gateway converge alla prossima passata.',
        current: 'Attuale',
        refusals: {
          notSupportedCeph:
            'Non applicabile a una share Ceph: usa il gatewayQos del cluster (per indirizzo di gateway).',
          notSupportedBidirectional:
            'Non applicabile a uno store bidirezionale: serve dagli indirizzi di gateway del cluster — usa il gatewayQos del cluster.',
          notSupportedBucketEngine: 'La QoS del bucket è un rate limit di RGW — questo motore non ne ha.',
          opsNotSupported:
            'Le operazioni al secondo non sono un tetto qui: gli IOPS sono la QoS del volume (set-volume-qos); versitygw non ha un rate limit.',
          invalidBps: 'Non è una banda: usa MiB/s (50, 1.5G, 2048K).',
          invalidOps: 'Non è un numero di operazioni al secondo (intero).',
          floorBps: 'Sotto il pavimento di 1 MiB/s.',
          floorOps: 'Sotto il pavimento di 1 op/s.'
        },
        volumeQos: 'QoS del volume ereditata (IOPS / MiB/s)',
        volumeQosNone: 'nessuna — il volume consumato non ha QoS',
        volumeQosCeph: 'non applicabile su CephFS (nessun limite di IOPS per share — mClock è per cluster)',
        volumeQosHint:
          'La metà IOPS della QoS della share vive sul volume a blocchi consumato (QoS-2): si cambia con set-volume-qos o nella scheda del volume.',
        status: {
          title: 'Applicato sul nodo',
          none: 'non ancora riportato',
          shaped: 'modellato su',
          notShaped: 'NON modellato su',
          rgwApplied: 'applicato per account',
          rgwNotApplied: 'non applicato dal gateway',
          observedAt: 'Osservato',
          hint: 'Lo stato è ciò che il nodo ha riletto dal kernel (tc) o dal demone — mai il desiderio: «non modellato» porta il motivo (QoS di rete disattivata sul device, device non derivabile, tc assente).'
        }
      },
      tabAudit: 'Audit',
      audit: {
        intro:
          'L’audit client registra le operazioni che i client fanno sulla share — protocollo, sorgente, utente, indirizzo del client, operazione, percorso (mai il contenuto), esito, byte — in UNA forma di evento: SMB tramite vfs_full_audit, WebDAV tramite il gate, NFS su knfsd tramite fanotify sul percorso esportato, NFS su Ganesha solo eventi di mount/unmount e autenticazione (FSAL in spazio utente: niente audit per file). Gli eventi finiscono nel file di audit del nodo proprietario, ruotano con il loro tetto, arrivano a ogni sink syslog che inoltra i log dei demoni e alimentano le metriche share.* dell’audit e le regole di fabbrica.',
        policy: {
          title: 'Policy',
          enabled: 'Audit client',
          on: 'Acceso',
          off: 'Spento (default)',
          operations: 'Operazioni registrate',
          operationsHint:
            'Nessuna selezionata = tutte le operazioni. Connessioni/autenticazioni/mount falliti e operazioni negate si registrano comunque (sicurezza).',
          costHint:
            'Spento di default: vfs_full_audit costa per operazione SMB e fanotify per accesso NFS — accendilo dove rende. Con l’audit acceso su share molto trafficate, il collettore syslog è il posto dove il flusso vive; il file locale è una coda.',
          save: 'Salva policy',
          saved:
            'Policy di audit salvata — i nodi serventi convergono al passaggio successivo (lo smbd della share si riavvia al cambio)'
        },
        operations: {
          connect: 'Connessione',
          mount: 'Mount',
          auth: 'Autenticazione',
          open: 'Apertura',
          read: 'Lettura',
          write: 'Scrittura',
          create: 'Creazione',
          delete: 'Cancellazione',
          rename: 'Rinomina',
          mkdir: 'Creazione cartella',
          rmdir: 'Rimozione cartella',
          setattr: 'Attributi',
          setacl: 'ACL',
          list: 'Elenco',
          lock: 'Lock'
        },
        tail: {
          title: 'Ultimi eventi',
          size: 'Eventi',
          refresh: 'Aggiorna',
          empty: 'Nessun evento — l’audit è spento, o nessun client ha operato da quando è acceso',
          truncated: 'Ci sono più eventi di quelli mostrati — il flusso completo è sul collettore syslog',
          footer: '{count} evento/i letti dal file di {node}: {file}',
          time: 'Ora',
          protocol: 'Protocollo / sorgente',
          user: 'Utente',
          client: 'Client',
          operation: 'Operazione',
          outcome: 'Esito',
          path: 'Percorso',
          bytes: 'Byte',
          security: 'sicurezza',
          unavailable:
            'Il nodo proprietario non ha risposto alla lettura della coda — gli eventi vivono nel suo file locale; riprova, o leggi il collettore syslog',
          forbidden: 'Leggere la coda dell’audit richiede il permesso audit.read su questa risorsa'
        },
        outcomes: {
          ok: 'ok',
          denied: 'negato',
          error: 'errore'
        }
      },

      // ENEA-A-R10 (2026-10-23): il catalogo dei metadati dei file della share.
      tabCatalog: 'Catalogo',
      catalog: {
        intro:
          'Il catalogo è un database PostgreSQL di ogni voce della share (percorso, tipo, taglia, proprietario, tempi, estensione), riempito da un crawler sul nodo proprietario della share e aperto in tre modi: questa scheda, l’export CSV/JSONL e un ruolo SQL in sola lettura per il tuo client SQL/BI. Opt-in per share: una scansione piena legge tutti i metadati della share e il database cresce sul nodo proprietario.',
        policy: {
          title: 'Policy',
          enabled: 'Catalogo',
          on: 'Acceso',
          off: 'Spento',
          everyMinutes: 'Ogni (minuti)',
          mode: 'Modo',
          modes: { auto: 'Auto', full: 'Piena', incremental: 'Incrementale (rctime)' },
          incrementalBlock: 'solo CephFS',
          fullEvery: 'Una piena ogni N scansioni',
          maxEntries: 'Tetto di voci',
          maxEntriesHint: 'Vuoto = nessun tetto (≈ 450–600 byte per voce sul nodo proprietario)',
          save: 'Salva la policy',
          saved:
            'Policy salvata — il worker del nodo proprietario converge al prossimo tick (la prima scansione è piena).',
          hint: 'Auto = incrementale per rctime ricorsivo di CephFS dove c’è, piena altrove; la prima scansione è sempre piena, e una piena ogni N tiene onesta l’euristica incrementale. Spegnere il catalogo conserva le righe già catalogate.'
        },
        status: {
          title: 'Stato',
          owner: 'Nodo proprietario',
          database: 'Database',
          notBorn: 'non ancora nato — il worker del nodo proprietario lo crea al primo tick con il catalogo acceso',
          totals: 'Totali',
          totalsLine: '{entries} voci · {files} file · {dirs} directory · {bytes}',
          none: 'nessuna scansione conclusa',
          lastScan: 'Ultima scansione',
          scanLine: '{entries} voci · {errors} errori · {skipped} sottoalberi saltati · {removed} rimosse',
          runningLine: 'Una scansione {mode} è in corso da {at}',
          nextDue: 'Prossima scansione',
          due: {
            disabled: 'Spento',
            notBorn: 'In attesa del database',
            running: 'In scansione',
            due: 'Scaduta',
            scheduled: 'Pianificata',
            neverScanned: 'Mai scansionata'
          }
        },
        scan: {
          action: 'Scansiona ora',
          started: 'Task di scansione {task} avviato sul nodo proprietario.',
          disabled: 'Accendi prima il catalogo',
          running: 'Una scansione è già in corso'
        },
        outcome: { ok: 'ok', partial: 'parziale', failed: 'fallita', cancelled: 'annullata', running: 'in corso' },
        modeLabel: { full: 'piena', incremental: 'incrementale' },
        kinds: { file: 'file', dir: 'directory', symlink: 'link simbolico', other: 'altro' },
        filters: {
          title: 'Voci',
          pathPrefix: 'Sotto la directory',
          extension: 'Estensione',
          type: 'Tipo',
          anyType: 'Qualsiasi',
          sizeMin: 'Taglia min (MiB)',
          sizeMax: 'Taglia max (MiB)',
          mtimeFrom: 'Modificato dal',
          mtimeTo: 'Modificato fino al',
          nameContains: 'Nome contiene',
          sort: 'Ordine',
          apply: 'Applica',
          reset: 'Azzera',
          page: 'Pagina {n}',
          firstPage: 'Prima pagina',
          next: 'Pagina successiva'
        },
        columns: {
          path: 'Percorso',
          type: 'Tipo',
          size: 'Taglia',
          mtime: 'Modificato',
          owner: 'uid:gid',
          mode: 'Permessi'
        },
        empty: 'Nessuna voce corrisponde a questi filtri.',
        summary: {
          title: 'Ripartizione',
          byExtension: 'Per estensione',
          byOwner: 'Per proprietario',
          byDirectory: 'Per directory di primo livello (file · sottodirectory · byte)',
          noExtension: '(nessuna)'
        },
        export: {
          csv: 'Esporta CSV',
          jsonl: 'Esporta JSONL',
          done: '{name} scaricato (valgono i filtri e l’ordine correnti).'
        },
        credentials: {
          action: 'Credenziali SQL',
          title: 'Credenziali SQL in sola lettura',
          intro:
            'Un ruolo per database di catalogo, in SOLA LETTURA: legge il catalogo di TUTTE le share di questo nodo proprietario e nient’altro. Il tuo client SQL/BI raggiunge il PostgreSQL del nodo proprietario dalla rete di management — TLS e la riga pg_hba per questo ruolo sono del deployment (manuale §6.11).',
          reveal: 'Mostra',
          tls: 'TLS offerto — certificato sha256 {fp}',
          tlsNone:
            'Il server oggi non offre TLS: configuralo prima di esporre questa porta oltre la rete di management.'
        }
      },
      snapshots: {
        intro:
          'Istantanee della share, con il nome che scegli tu e visibili ai client sotto la directory .snap (mount NFS del kernel e CephFS; per Ganesha vedi il manuale). Ognuna può portare una retention congelata. Gli snapshot pianificati delle Versioni precedenti sono una famiglia separata e non compaiono qui.',
        create: 'Crea snapshot',
        createTitle: 'Snapshot della share {name}',
        name: 'Nome',
        nameHint: 'Lettere minuscole, cifre e trattini — unico nella share',
        cephName: 'Nome nel cluster',
        empty: 'Nessuno snapshot',
        notReady: 'Solo gli snapshot Pronti ammettono questa azione',
        shareNotReady: 'La share deve essere Pronta prima di poterne scattare uno snapshot',
        multiscsiBlock:
          'Una share MultiSCSI non ha snapshot propri: lì la share È il volume — scatta lo snapshot sul volume (Volumi → Snapshot).',
        clone: 'Clona in una nuova share',
        cloneTitle: 'Clona lo snapshot {name}',
        cloneHint:
          'Il clone è una share NUOVA e indipendente sullo stesso volume: dati propri copiati dallo snapshot, configurazione della share sorgente (protocolli, utenti, modello ACL) riportata. Cancellare poi lo snapshot non lo invalida mai; solo mentre il clone sta nascendo la cancellazione dello snapshot è rifiutata.',
        cloneName: 'Nome della nuova share',
        cloneSize: 'Dimensione (GiB)',
        cloneSizeHint: 'La quota propria della nuova share sul volume; GiB interi',
        deleteBody:
          "Lo snapshot e i suoi dati all'istante vengono rimossi (lo esegue un task). Uno snapshot in retention o con un clone ancora in nascita è rifiutato dal server, con il motivo."
      },
      acl: {
        path: 'Percorso',
        pathHint: 'Relativo alla radice della condivisione — vuoto è la radice stessa',
        load: 'Leggi ACL',
        loading: 'Leggo la ACL dal nodo che serve…',
        lossyForChip: '{protocol} vede una vista con perdita',
        daclAbsent:
          'Su questo percorso nessuna ACL ricca è ancora stata scritta: assente NON è vuota — i permessi effettivi sono i bit POSIX sottostanti (una ACL vuota significherebbe nessuno-niente).',
        owner: 'Proprietario (SID)',
        group: 'Gruppo (SID)',
        ownerHint: "Vuoto = lascia quello che c'è",
        kind: 'Tipo',
        kinds: {
          allow: 'Concedi',
          deny: 'Nega',
          audit: 'Audit (SACL)'
        },
        trustee: 'Soggetto',
        trusteeHintNt: "Un SID (S-1-5-21-…) — il nome che sopravvive al rename dell'utente e al cambio di dominio",
        trusteeHintPosix: 'owner@, group@, everyone@, mask@, user:<uid> o group:<gid>',
        rightsLabel: 'Diritti',
        rights: {
          readData: 'Leggi dati',
          writeData: 'Scrivi dati',
          appendData: 'Accoda',
          readEa: 'Leggi EA',
          writeEa: 'Scrivi EA',
          execute: 'Esegui',
          deleteChild: 'Elimina figli',
          readAttributes: 'Leggi attributi',
          writeAttributes: 'Scrivi attributi',
          delete: 'Elimina',
          readControl: 'Leggi controllo',
          writeDac: 'Scrivi DAC',
          writeOwner: 'Cambia proprietario',
          synchronize: 'Sincronizza',
          accessSystemSecurity: 'Sicurezza di sistema',
          maximumAllowed: 'Massimo consentito',
          genericAll: 'Generico tutto',
          genericExecute: 'Generico esecuzione',
          genericWrite: 'Generico scrittura',
          genericRead: 'Generico lettura'
        },
        inheritLabel: 'Ereditarietà',
        inherit: {
          objectInherit: 'Eredita su oggetti',
          containerInherit: 'Eredita su cartelle',
          noPropagateInherit: 'Senza propagazione',
          inheritOnly: 'Solo ereditata',
          inherited: 'Ereditata',
          auditSuccess: 'Audit sui successi',
          auditFailure: 'Audit sui fallimenti'
        },
        lossCodes: {
          denyAce: 'Voce di nega',
          aceOrder: 'Ordine delle voci',
          granularRights: 'Diritti granulari',
          appendWithoutWrite: 'Accoda senza scrittura',
          richInheritance: 'Ereditarietà ricca',
          inheritOnly: 'Solo-ereditata',
          noPropagateInherit: 'Ereditarietà senza propagazione',
          posixMask: 'Maschera POSIX',
          creatorTrustee: 'Soggetto creator',
          auditing: 'Audit (SACL)',
          unmappableTrustee: 'Soggetto non mappabile',
          posixMaskWidening: 'Allargamento della maschera POSIX',
          defaultAclWidening: 'Allargamento della ACL di default'
        },
        addEntry: 'Aggiungi voce',
        removeEntry: 'Rimuovi voce',
        writeModel: 'Scrivi come',
        writeModelHint:
          'Nativa (posix) scrive senza traduzione; le voci NT su questa condivisione posix vengono tradotte — una perdita che restringe chiede la spunta, una perdita che APRIREBBE un accesso è rifiutata e basta.',
        writeModelNtShare:
          "L'autorità di questa condivisione è il descrittore NT: scrivere voci POSIX qui butterebbe via la ACL ricca, quindi si offre solo la scrittura NT (posix la rifiuta l'API).",
        notRepresentable:
          'Non rappresentabile — nessuna spunta la rende vera: correggi le voci che il messaggio nomina.',
        save: 'Salva ACL'
      }
    },

    // NAS-3 (2026-08-05, PROPOSAL in attesa di ratifica): il servizio DHCPv4
    // Kea OPZIONALE gestito dalla piattaforma, ancorato a una condivisione
    // MultiSCSI con TFTP. Ogni mutazione è sudo-gated — l'avvertenza sul
    // servizio invasivo compare ovunque parta una mutazione.
    dhcp: {
      nav: 'DHCP/PXE',
      title: 'Servizi DHCP',
      createTitle: 'Crea servizio DHCP',
      invasiveWarning:
        "Un server DHCP risponde a OGNI macchina del segmento, e due server autoritativi sulla stessa rete si corrompono i lease a vicenda. Se questa sede ha già un DHCP, NON crearne uno qui — configura invece le opzioni 66/67 (next-server, filename) sul server esistente; la piattaforma non può rilevare i DHCP altrui, quindi questa avvertenza è tutta la protezione che c'è.",
      optionalHint:
        'Opzionale per progetto: esiste solo per le reti SENZA un DHCP. Il demone gira sul nodo authority della condivisione di ancoraggio, legato al suo indirizzo di servizio flottante, e lo segue al failover.',
      name: 'Nome',
      nameHint: 'Minuscole, cifre e trattini — cosmetico, niente sul filo lo incorpora',
      fileShare: 'Condivisione di ancoraggio',
      fileShareHint:
        'Una condivisione MultiSCSI con TFTP tra i protocolli — la catena PXE (risposta DHCP, fetch TFTP, boot) punta al suo indirizzo di servizio; immutabile dopo la creazione',
      noShares:
        'Nessuna condivisione MultiSCSI con TFTP esiste — creane una prima (il protocollo tftp su una condivisione file).',
      subnet: 'Subnet (CIDR)',
      subnetHint:
        'La subnet IPv4 servita, es. 192.0.2.0/24 — unica a livello di cluster: un servizio per subnet, sempre',
      poolFrom: 'Pool da',
      poolTo: 'Pool a',
      poolHint: 'Il range di lease dinamici, dentro la subnet',
      leaseSeconds: 'Lease (secondi)',
      leaseHint: 'Vuoto = 3600',
      routers: 'Gateway (opzione 3)',
      routersHint:
        "Lista di indirizzi IPv4 separati da virgola, in ordine — vuota: l'opzione non viene inviata (onesto per segmenti di sola installazione)",
      dnsServers: 'Server DNS (opzione 6)',
      dnsHint: 'Lista separata da virgola — vuota: non inviata',
      domainName: 'Dominio (opzione 15)',
      bootEnabled: 'Invia le opzioni di boot PXE (66/67)',
      bootHint:
        "Ogni offerta porta next-server/filename, divisi per architettura del client (option 93): i client BIOS ricevono filename (default pxelinux.0), i client UEFI x64 ricevono efiFilename (default grubx64.efi). I campi vuoti significano quei default; next-server vale l'indirizzo di servizio della condivisione di ancoraggio.",
      nextServer: 'Next-server (opzione 66)',
      filename: 'File di boot (opzione 67)',
      efiFilename: 'File di boot UEFI (opzione 67)',
      efiFilenameHint:
        'Per i client UEFI x64 (opzione 93 = 0x0007/0x0009); vuoto = grubx64.efi. Puntalo allo shim caricato per i client Secure Boot.',
      reservationsHint:
        'Prenotazioni statiche MAC→indirizzo — MAC in forma minuscola con due punti, indirizzi dentro la subnet; MAC e indirizzi unici nel servizio.',
      reservationMac: 'MAC',
      reservationAddress: 'Indirizzo',
      reservationHostname: 'Hostname (opzione 12)',
      reservationAdd: 'Aggiungi prenotazione',
      editTitle: 'Modifica il servizio DHCP {name}',
      editHint:
        'Ogni modifica che tocca il servizio è una scrittura di config che converge con la passata di riconciliazione (re-render di Kea + reload del demone). Un cambio di subnet riesegue il controllo di unicità.',
      fileShareImmutable: 'Immutabile — riancorare è un servizio nuovo',
      subnetEditHint: 'CIDR IPv4 — un cambio riesegue il controllo di unicità (409 in collisione)',
      enabled: 'Attivo',
      parked: 'Parcheggiato',
      parkHint:
        "Disattivato = parcheggiato: configurazione mantenuta, demone fermo — l'alternativa onesta all'eliminazione quando torna il DHCP del cliente.",
      phase: 'Fase',
      phases: {
        pending: 'In attesa',
        running: 'In esecuzione',
        degraded: 'Degradato',
        stopped: 'Fermo',
        error: 'Errore'
      },
      leaseCount: 'Lease',
      daemonNode: 'Nodo di servizio',
      daemonState: 'Demone',
      reservations: 'Prenotazioni',
      bootOptions: 'Opzioni di boot',
      bootNone: 'Nessuna — solo lease (non fa il boot a nessuno)',
      observedAt: 'Osservato il',
      notObserved: 'Non ancora osservato',
      relayWarningTitle: 'Avvertenza del server',
      toggle: {
        parkAction: 'Parcheggia (ferma il demone)',
        resumeAction: 'Riattiva',
        parkTitle: 'Parcheggiare il servizio DHCP {name}?',
        resumeTitle: 'Riattivare il servizio DHCP {name}?',
        parkText:
          'Il demone si ferma; la configurazione resta. Le macchine con un lease lo mantengono fino alla scadenza.',
        resumeText:
          'Il demone riparte sul nodo authority della condivisione di ancoraggio. Assicurati che nessun altro DHCP serva questa subnet.',
        submitPark: 'Parcheggia',
        submitResume: 'Riattiva'
      },
      delete: {
        title: 'Eliminare il servizio DHCP {name}?',
        warning:
          'Sincrono: la riga sparisce e il pass di riconciliazione ferma il demone Kea. Le macchine con un lease lo mantengono fino alla scadenza — un DHCP che sparisce è innocuo per loro.'
      },
      sudoRequired: 'Ogni mutazione DHCP è sudo-gated — inserisci la tua password per elevare.'
    },
    localDisks: {
      title: 'Dischi locali',
      node: 'Nodo',
      nodeHint: 'Dischi, chunk e raid sono locali al nodo — scegli il nodo di cui gestire lo stack',
      noNodes: 'Nessun nodo ancora nel registry del cluster',
      tabs: {
        disks: 'Dischi',
        raids: 'Raid'
      },
      disks: {
        markSelected: 'Marca usabili',
        unmarkSelected: 'Smarca',
        markAndChunk: "Marca per l'uso"
      },
      chunks: {
        censusDenied: 'Leggere il censimento dei chunk richiede il permesso chunk.read.'
      },
      markChunk: {
        markTitle: "Marca {device} per l'uso e dividilo in chunk",
        chunkTitle: 'Dividi {device} in chunk',
        note: "Un solo flusso: il disco viene marcato usabile E diviso in chunk anonimi uguali. Scegli IN QUANTI chunk — la taglia per chunk che la API prende (GiB interi) è derivata per divisione intera, e il censimento conferma il totale finale dopo la suddivisione (l'overhead dei metadati LVM può limare la coda).",
        countLabel: 'Numero di chunk',
        countHint: 'La taglia del chunk è derivata: floor(GiB del disco / numero)',
        tooMany: 'Troppi chunk — la taglia derivata scende sotto 1 GiB',
        direct: 'Inserisci direttamente la taglia',
        derivedRegime: 'Regime risultante: {regime}',
        markFailedPrefix: 'Marcatura fallita — nessuna suddivisione eseguita',
        chunkFailedPrefix:
          'Il disco ora è MARCATO ma la suddivisione è fallita — riprova con Crea chunk sulla sua riga',
        submitBoth: 'Marca e dividi'
      }
    },
    vdisks: {
      title: 'vDisk',
      expand: {
        action: 'Espandi',
        title: 'Espandi il vdisk {name}',
        hint: "Solo crescita, a chunk interi della taglia del disco — lo shrink non esiste, mai. Solo forma JBOD in questo round (l'espansione raid arriva con DISK-1.2). Su un vdisk legato a un cluster il task segue la capacità su per lo stack.",
        addChunks: 'Chunk aggiuntivi',
        result: 'Da {current} GiB a {target} GiB',
        submit: 'Espandi',
        raidBlock: "L'espansione della forma RAID arriva con DISK-1.2 — in questo round si espandono solo i vdisk jbod",
        notReady: 'Si espande solo un vdisk pronto',
        noCensus:
          'Il censimento dei chunk non è disponibile (chunk.read) — la taglia di destinazione non si può calcolare qui.'
      },

      // GUI-VERBI DISK-2 #50 (2026-08-29): svuotare un disco membro del
      // raid su un altro disco marcato, A CALDO — il gesto di svuotamento
      // dietro la rimozione di un disco (mdadm --replace: il kernel copia
      // dai membri vivi, nessuna finestra degradata — misurato sul banco).
      migrate: {
        action: 'Migra disco membro',
        title: 'Migra un disco membro di {name}',
        submit: 'Avvia migrazione',
        hint: "Sposta la parte di UN disco membro del raid su un altro disco marcato dello stesso nodo, A CALDO — l'array resta in servizio (mdadm --replace copia dai membri vivi, nessuna finestra degradata). Il sorgente si libera solo a copia convergente.",
        jbodBlock:
          "Un jbod si migra col mirror cross-nodo — la forma raid è l'unica corsia di replace a caldo (ratifica 2026-08-28)",
        externalBlock: 'Una LUN esterna non ha chunk locali da migrare — la sua ridondanza è della SAN terza',
        noRaid: 'Il censimento raid non è disponibile (raid.read) — la lista membri non si può mostrare qui.',
        noCandidates:
          'Nessun disco destinazione: ogni disco marcato e a chunk di questo nodo è già membro di questo raid.',
        from: 'Disco membro da svuotare',
        fromHint: 'Un membro attuale del raid del vdisk',
        to: 'Disco destinazione',
        toHint: 'Un disco marcato dello stesso nodo, non già membro',
        candidate: '{disk} — {free} chunk liberi × {size} GiB',
        fit: 'Servono {needed} chunk sulla destinazione — {free} liberi.',
        noFit:
          "Servono {needed} chunk sulla destinazione ma i liberi sono {free} — l'ammissione rifiuterà (l'allocatore parla con gli stessi numeri).",
        busyHint:
          "L'array sta copiando (replace/ricostruzione in corso) — un vdisk in migrazione resta in servizio; il progresso è la verità del device."
      }
    },
    enclosures: {
      title: 'Enclosure',
      summary:
        'Cassetti NVMe dual-port condivisi fra due teste: un proprietario per disco, failover manuale col fence confermato prima, rientro ordinato.',
      empty:
        'Nessuna enclosure dichiarata — la dichiara un operatore con POST /api/v1/enclosures (CLI: create-enclosure).',
      expand: 'Mostra i dischi',
      collapse: 'Nascondi i dischi',
      columns: {
        name: 'Nome',
        phase: 'Fase',
        heads: 'Teste',
        reserve: 'Riserva',
        disks: 'Dischi',
        wwid: 'WWID',
        serial: 'Seriale',
        model: 'Modello',
        size: 'Dimensione',
        owner: 'Proprietario',
        vdisk: 'vDisk',
        osd: 'OSD',
        seenBy: 'Visto da'
      },
      phases: {
        ready: 'Pronta',
        degraded: 'Degradata',
        'failing-over': 'Failover in corso',
        error: 'Errore',
        deleting: 'In eliminazione'
      },
      actions: {
        failover: 'Failover…',
        failback: 'Failback…'
      },
      failover: {
        title: 'Failover — {name}',
        warning:
          'La doppia attivazione dello stesso disco corrompe BlueStore: il fence della testa morta deve aver avuto effetto PRIMA dell’attivazione sulla superstite. Senza la conferma la richiesta è rifiutata.',
        from: 'Da (la testa morta)',
        to: 'A (la testa superstite)',
        method: 'Metodo di fence',
        methods: {
          'power-off': 'Spegnimento (corrente staccata)',
          'nvme-reservation': 'Reservation NVMe sui namespace',
          'pcie-port': 'Porte dello switch PCIe disabilitate',
          bmc: 'Spegnimento via BMC (Redfish/IPMI)',
          declared: 'Dichiarato dall’operatore'
        },
        confirmed: 'Confermo che il fence della testa ha avuto effetto',
        note: 'Nota (messa a verbale nel task)',
        submit: 'Avvia il failover'
      },
      failback: {
        title: 'Failback — {name}',
        hint: 'Entrambe le teste vive: {from} rilascia i dischi (OSD fermati, VG disattivato), poi {to} li importa.',
        from: 'Da (il proprietario attuale)',
        to: 'A (la testa rientrata)',
        submit: 'Avvia il failback'
      },
      started: 'Task avviato: {task}',
      startedNoTask: 'Richiesta accettata — nessun riferimento al task restituito.',
      taskLink: 'Apri il task'
    },
    placeholders: {
      remoteDisks: {
        title: 'Dischi remoti',
        body: 'I dischi iSCSI/NVMe-TCP di terze parti per backup e replica arrivano col round EXT-1.'
      }
    }
  }
}
